/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Button, Card, Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, TextField, InputAdornment, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableHead, TableRow,
  Autocomplete, ToggleButton, ToggleButtonGroup, Divider, Alert, Typography,
  Radio, Checkbox, FormControlLabel, Paper, Tooltip, Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import PaymentIcon from '@mui/icons-material/Payment';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useNavigate, useParams } from 'react-router-dom';
import { usePharmacy } from '../../../contexts/PharmacyContext';
import TimelineDialog from '../components/TimelineDialog';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../../api';
import KeyboardHintBar from 'components/KeyboardHintBar';
import { formKeyHandler } from 'utils/formKeyboard';
import DebitNoteDialog from './DebitNoteDialog';
import irmHeader from 'assets/images/irm-header.png';
import printReturnNote from './printReturnNote';
import { codeBarcodeStack, barcodeScriptBlock, documentCodeValue } from 'utils/printBarcodes';
import printPaymentReceipt from './printPaymentReceipt';
import printCreditNote from './printCreditNote';

const TEAL = '#00798E';
const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtExpiry = (v) => {
  if (!v) return '—';
  const [yr, mo] = String(v).split('-');
  return (yr && mo) ? `${MONTHS[parseInt(mo, 10) - 1]} ${yr}` : v;
};
const todayStr = () => new Date().toISOString().slice(0, 10);
const isExpiredDate = (d) => d && new Date(d) < new Date(todayStr());

// ─── Calculation helpers ───────────────────────────────────

function calcLineTotals(item) {
  const qty = Number(item.qty_base_deducted || 0);
  const mrp = Number(item.mrp || 0);
  const disc = Math.min(Number(item.discount_percentage || 0), 100);
  const cgstPct = Number(item.cgst_percentage || 0);
  const sgstPct = Number(item.sgst_percentage || 0);
  const igstPct = Number(item.igst_percentage || 0);
  const cessPct = Number(item.cess_percentage || 0);
  const gstPct = cgstPct + sgstPct + igstPct + cessPct;
  // MRP is GST-inclusive — back it out to the true pre-tax base first, apply
  // the discount there, then re-derive CGST/SGST/IGST/Cess off that
  // discounted base. Discounting the inclusive gross directly (old code)
  // left GST still baked into "taxable", so tax got charged a second time
  // on top of an already-tax-inclusive figure (e.g. MRP 148.53 less 10% at
  // 5% GST landed on 140.36 instead of the correct 133.68).
  const gross = mrp * qty;
  const exclBase = gstPct > 0 ? gross / (1 + gstPct / 100) : gross;
  const discAmtExcl = exclBase * (disc / 100);
  const taxable = exclBase - discAmtExcl;
  const cgst = taxable * (cgstPct / 100);
  const sgst = taxable * (sgstPct / 100);
  const igst = taxable * (igstPct / 100);
  const cess = taxable * (cessPct / 100);
  const lineTotal = taxable + cgst + sgst + igst + cess;
  const salePrice = qty > 0 ? lineTotal / qty : mrp * (1 - disc / 100);
  return {
    ...item,
    gst_percentage: gstPct,
    sale_price: salePrice,
    taxable_amount: taxable,
    cgst_amount: cgst,
    sgst_amount: sgst,
    igst_amount: igst,
    cess_amount: cess,
    line_total: lineTotal,
  };
}

function calcBillTotals(items, roundOffEnabled = true) {
  const t = items.reduce((acc, it) => ({
    sub_total: acc.sub_total + Number(it.mrp || 0) * Number(it.qty_base_deducted || 0),
    // Discount in inclusive-of-tax terms (gross MRP vs what the line
    // actually comes to) — NOT gross minus taxable_amount, since taxable_
    // amount is now GST-exclusive while gross MRP is GST-inclusive; mixing
    // those bases would silently fold the backed-out GST into "discount".
    total_discount: acc.total_discount + (Number(it.mrp || 0) * Number(it.qty_base_deducted || 0) - Number(it.line_total || 0)),
    taxable_amount: acc.taxable_amount + Number(it.taxable_amount || 0),
    total_cgst: acc.total_cgst + Number(it.cgst_amount || 0),
    total_sgst: acc.total_sgst + Number(it.sgst_amount || 0),
    total_igst: acc.total_igst + Number(it.igst_amount || 0),
    total_cess: acc.total_cess + Number(it.cess_amount || 0),
    net_amount: acc.net_amount + Number(it.line_total || 0),
  }), { sub_total: 0, total_discount: 0, taxable_amount: 0, total_cgst: 0, total_sgst: 0, total_igst: 0, total_cess: 0, net_amount: 0 });
  // User-facing round-off: net_amount rounded to the nearest rupee, with the
  // signed delta carried in round_off_amount. raw_net_amount keeps the exact
  // sum for display of the "Sub Total" line.
  const rawNet = Number(t.net_amount.toFixed(2));
  const applyRoundOff = roundOffEnabled !== false;
  return {
    ...t,
    raw_net_amount: rawNet,
    round_off_amount: applyRoundOff ? Number((Math.round(rawNet) - rawNet).toFixed(2)) : 0,
    net_amount: applyRoundOff ? Math.round(rawNet) : rawNet,
  };
}

// Build a cart item from an inventory batch + qty info
function buildCartItem(inv, baseQty, unitType, displayQty, defaultDisc, sortOrder, isInterstate = false) {
  const item = inv.item || {};
  const variant = inv.variant || null;
  const hsn = item.hsn_code || null;
  const totalGst = Number(hsn?.gst_percentage || 0);
  const cessPct = Number(hsn?.cess_percentage ?? 0);
  const gstExCess = totalGst - cessPct;
  // Intrastate → split into CGST + SGST; Interstate → full amount goes to IGST
  const cgstPct = isInterstate ? 0 : (hsn?.cgst_percentage ?? gstExCess / 2);
  const sgstPct = isInterstate ? 0 : (hsn?.sgst_percentage ?? gstExCess / 2);
  const igstPct = isInterstate ? (hsn?.igst_percentage || gstExCess) : 0;
  const disc = Number(defaultDisc ?? item.discount_percentage ?? 0);

  const base = {
    _key: `${inv.id}_${Date.now()}_${Math.random()}`,
    irm_pharm_items_id: item.id,
    irm_pharm_item_variants_id: variant?.id || null,
    irm_pharm_inventory_id: inv.id,
    item_name: item.name,
    item_code: item.item_code || null,
    variant_name: variant?.variant_name || null,
    barcode: inv.barcode || null,
    batch_no: inv.batch_no,
    expiry_date: inv.expiry_date,
    hsn_code: hsn?.hsn_code || null,
    unit_type: unitType,
    qty_units: unitType === 'BASE' ? displayQty : 0,
    qty_sub_units: unitType === 'SUB' ? displayQty : 0,
    qty_base_deducted: baseQty,
    mrp: Number(inv.mrp),
    sale_price: 0,
    discount_percentage: disc,
    gst_percentage: (cgstPct + sgstPct + igstPct + cessPct),
    cgst_percentage: cgstPct,
    sgst_percentage: sgstPct,
    igst_percentage: igstPct,
    cess_percentage: cessPct,
    taxable_amount: 0, cgst_amount: 0, sgst_amount: 0, igst_amount: 0, cess_amount: 0, line_total: 0,
    returned_qty: 0, is_returned: 'N',
    // display-only fields (stripped before API call)
    _base_unit: item.base_unit?.name || 'Unit',
    _sub_unit: item.sub_unit?.name || null,
    _sub_per_base: Number(item.sub_units_per_base || 1),
    _is_sub_sellable: item.is_sub_unit_sellable === 'Y',
    _max_stock: Number(inv.qty_base_units),
    sort_order: sortOrder,
  };
  return calcLineTotals(base);
}

// ─── Section header ────────────────────────────────────────

function SectionHeader({ icon, title }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1, borderBottom: '1.5px solid #f0f2f5' }}>
      {icon}
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: TEAL, letterSpacing: 0.4, textTransform: 'uppercase' }}>{title}</Typography>
    </Box>
  );
}

// ─── Payment Dialog ────────────────────────────────────────

function PaymentDialog({ open, netAmount, creditNotes, onClose, onConfirm }) {
  const [payments, setPayments] = useState([{ payment_mode: 'CASH', amount: '', reference_no: '' }]);
  const [selectedCNs, setSelectedCNs] = useState([]);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setPayments([{ payment_mode: 'CASH', amount: String(Number(netAmount).toFixed(2)), reference_no: '' }]);
      setSelectedCNs([]);
      setErr('');
    }
  }, [open, netAmount]);

  const creditTotal = selectedCNs.reduce((s, cn) => s + Number(cn.balance_amount || 0), 0);
  const payTotal = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const covered = payTotal + creditTotal;

  const addRow = () => setPayments(p => [...p, { payment_mode: 'CASH', amount: '', reference_no: '' }]);
  const removeRow = (i) => setPayments(p => p.filter((_, j) => j !== i));
  const updateRow = (i, f, v) => setPayments(p => p.map((x, j) => j === i ? { ...x, [f]: v } : x));
  const toggleCN = (cn) => setSelectedCNs(s => s.find(x => x.id === cn.id) ? s.filter(x => x.id !== cn.id) : [...s, cn]);

  const handleConfirm = () => {
    for (const p of payments) {
      if (!p.amount || isNaN(+p.amount) || +p.amount <= 0) { setErr('All payment amounts must be valid positive numbers.'); return; }
      if (p.payment_mode !== 'CASH' && !p.reference_no?.trim()) { setErr(`Reference number is required for ${p.payment_mode}.`); return; }
    }
    if (covered < Number(netAmount) - 0.01) { setErr(`Total covered (${fmt(covered)}) is less than net amount (${fmt(netAmount)}).`); return; }
    onConfirm({ payments: payments.map(p => ({ ...p, amount: +p.amount, payment_date: todayStr() })), credit_note_ids: selectedCNs.map(c => c.id) });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ background: `linear-gradient(135deg, ${TEAL}, #005f70)`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon sx={{ fontSize: 20 }} />
          <span style={{ fontSize: '0.95rem', fontWeight: 700 }}>Finalize — Add Payment</span>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5, px: 2.5 }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' || e.shiftKey) return;
          const tag = (e.target.tagName || '').toLowerCase();
          if (tag === 'textarea') return;
          if (e.target.closest && e.target.closest('[aria-expanded="true"]')) return;
          e.preventDefault();
          handleConfirm();
        }}>
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, background: '#f7fbfc', border: `1px solid #cce8ed`, display: 'flex', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '0.8rem', color: '#555' }}>Net Amount</Typography>
          <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: TEAL, fontSize: '1rem' }}>{fmt(netAmount)}</Typography>
        </Box>

        {creditNotes?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', mb: 1, textTransform: 'uppercase', letterSpacing: 0.4 }}>Available Credit Notes</Typography>
            {creditNotes.map(cn => (
              <Box key={cn.id} onClick={() => toggleCN(cn)}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.2, mb: 0.75, borderRadius: 2, border: `1.5px solid ${selectedCNs.find(x => x.id === cn.id) ? TEAL : '#e0e0e0'}`, cursor: 'pointer', background: selectedCNs.find(x => x.id === cn.id) ? '#e8f5f8' : '#fafafa' }}>
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{cn.credit_note_no}</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#888' }}>Balance: {fmt(cn.balance_amount)} | {fmtDate(cn.credit_note_date)}</Typography>
                </Box>
                {selectedCNs.find(x => x.id === cn.id) && <CheckCircleIcon sx={{ color: TEAL, fontSize: 18 }} />}
              </Box>
            ))}
          </Box>
        )}

        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', mb: 1, textTransform: 'uppercase', letterSpacing: 0.4 }}>Payment Entries</Typography>
        {payments.map((p, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel sx={{ fontSize: '0.82rem' }}>Mode</InputLabel>
              <Select value={p.payment_mode} label="Mode" onChange={e => updateRow(i, 'payment_mode', e.target.value)}
                sx={{
                  fontSize: '0.82rem', borderRadius: 2,
                  '& .MuiSelect-select': {
                    py: '9.5px',
                    fontWeight: 600,
                    color: '#1a202c',
                  },
                }}>
                {['CASH', 'UPI', 'CARD', 'ADVANCE'].map(m => <MenuItem key={m} value={m} sx={{ fontSize: '0.82rem', py: 1 }}>{m}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField size="small" label="Amount" type="number" value={p.amount} autoFocus={i === 0} onChange={e => updateRow(i, 'amount', e.target.value)}
              onFocus={e => e.target.select()}
              sx={{ width: 110, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.78rem' } }} />
            {p.payment_mode !== 'CASH' && (
              <TextField size="small" label="Reference No *" value={p.reference_no} onChange={e => updateRow(i, 'reference_no', e.target.value)}
                sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.78rem' } }} />
            )}
            {payments.length > 1 && <IconButton size="small" onClick={() => removeRow(i)} sx={{ color: '#e74c3c', mt: 0.5 }}><DeleteIcon fontSize="small" /></IconButton>}
          </Box>
        ))}
        <Button size="small" startIcon={<AddIcon />} onClick={addRow} sx={{ color: TEAL, fontSize: '0.75rem', mb: 2, '&:hover': { color: TEAL, background: `${TEAL}0d` } }}>Add Payment Row</Button>

        <Box sx={{ p: 1.5, borderRadius: 2, background: covered >= Number(netAmount) - 0.01 ? '#e8f5e9' : '#fff3e0', border: `1px solid ${covered >= Number(netAmount) - 0.01 ? '#a5d6a7' : '#ffcc80'}`, display: 'flex', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '0.8rem' }}>Total Covered</Typography>
          <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.9rem', color: covered >= Number(netAmount) - 0.01 ? '#2e7d32' : '#e65100' }}>{fmt(covered)}</Typography>
        </Box>
        {err && <Alert severity="error" sx={{ mt: 1.5, fontSize: '0.78rem', py: 0.5 }}>{err}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button onClick={onClose} sx={{ fontSize: '0.78rem', color: '#666', '&:hover': { color: '#666', background: 'rgba(0,0,0,0.04)' } }}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained"
          sx={{ background: TEAL, '&:hover': { background: '#005f70', color: '#fff !important' }, fontSize: '0.78rem', fontWeight: 700, px: 3, borderRadius: 2, color: '#fff !important' }}>
          Confirm &amp; Finalize
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Cancel Dialog ─────────────────────────────────────────

function CancelDialog({ open, bill, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [refundMethod, setRefundMethod] = useState('CREDIT_NOTE');
  const refundable = Number(bill?.amount_paid || 0) > 0;

  const handleClose = () => { setReason(''); setRefundMethod('CREDIT_NOTE'); onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.18)' } }}>

      {/* ── Danger header ── */}
      <Box sx={{
        background: 'linear-gradient(135deg, #c62828 0%, #b71c1c 100%)',
        px: 3, pt: 3, pb: 2.5, position: 'relative', textAlign: 'center',
      }}>
        {/* Warning icon circle */}
        <Box sx={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'rgba(255,255,255,0.12)', border: '2px solid rgba(255,255,255,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mx: 'auto', mb: 1.5,
        }}>
          <Typography sx={{ fontSize: 30, lineHeight: 1 }}>⚠️</Typography>
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#fff !important', letterSpacing: 0.3 }}>
          Cancel This Bill?
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.72) !important', mt: 0.5 }}>
          This action cannot be undone. Stock will be restored.
        </Typography>
        <IconButton size="small" onClick={handleClose}
          sx={{ position: 'absolute', top: 10, right: 10, color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.12)' } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        {/* Reason field */}
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Reason for Cancellation
        </Typography>
        <TextField
          autoFocus
          fullWidth multiline rows={2} size="small"
          placeholder="e.g. Wrong items billed, patient refused…"
          value={reason} onChange={e => setReason(e.target.value)}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2, fontSize: '0.82rem',
              '&.Mui-focused fieldset': { borderColor: '#c62828' },
            },
          }}
        />

        {/* Refund method — only relevant if something was actually paid */}
        {refundable && (
          <Box>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#718096', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Refund ₹{Number(bill.amount_paid).toFixed(2)} via
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {[
                { key: 'CASH', label: 'Cash' },
                { key: 'CREDIT_NOTE', label: 'Credit Note' },
                { key: 'ONLINE', label: 'Online Transfer' },
              ].map(opt => (
                <Button key={opt.key} size="small" fullWidth
                  onClick={() => setRefundMethod(opt.key)}
                  variant={refundMethod === opt.key ? 'contained' : 'outlined'}
                  sx={{
                    fontSize: '0.73rem', borderRadius: 2, fontWeight: 600, py: 0.5, textTransform: 'none',
                    ...(refundMethod === opt.key
                      ? { background: '#c62828', color: '#fff !important', borderColor: '#c62828', '&:hover': { background: '#8e0000', color: '#fff !important' } }
                      : { color: '#4a5568', borderColor: '#c8d0d8', '&:hover': { borderColor: '#c62828', color: '#c62828' } }),
                  }}>
                  {opt.label}
                </Button>
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1.5, gap: 1 }}>
        <Button onClick={handleClose} fullWidth variant="outlined"
          sx={{
            borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem',
            borderColor: '#ddd', color: '#555 !important',
            '&:hover': { borderColor: '#aaa', background: '#f5f5f5' },
          }}>
          Keep Bill
        </Button>
        <Button
          onClick={() => { onConfirm({ cancel_reason: reason, refund_method: refundable ? refundMethod : undefined }); setReason(''); setRefundMethod('CREDIT_NOTE'); }}
          fullWidth variant="contained"
          sx={{
            borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: '0.82rem',
            background: 'linear-gradient(135deg, #c62828, #b71c1c)',
            color: '#fff !important',
            boxShadow: '0 4px 14px rgba(198,40,40,0.35)',
            '&:hover': { background: 'linear-gradient(135deg, #d32f2f, #c62828)', boxShadow: '0 6px 20px rgba(198,40,40,0.5)' },
          }}>
          Yes, Cancel Bill
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Return Dialog ─────────────────────────────────────────

function ReturnDialog({ open, bill, onClose, onConfirm }) {
  const [items, setItems] = useState([]);
  const [reason, setReason] = useState('');
  const [refundMethod, setRefundMethod] = useState('CASH');

  useEffect(() => {
    if (open && bill) {
      setItems((bill.items || []).filter(i => i.is_returned !== 'Y').map(i => ({
        irm_pharm_bill_items_id: i.id,
        irm_pharm_inventory_id: i.irm_pharm_inventory_id,
        unit_type: i.unit_type,
        sale_price: i.sale_price,
        return_qty: 0, return_base_qty: 0, refund_amount: 0,
        _name: i.item_name + (i.variant_name ? ` (${i.variant_name})` : ''),
        _batch: i.batch_no,
        _exp: i.expiry_date,
        _max: Number(i.qty_base_deducted) - Number(i.returned_qty || 0),
        _sub_per_base: i._sub_per_base || 1,
        _selected: false,
      })));
      setReason('');
      setRefundMethod('CASH');
    }
  }, [open, bill]);

  const toggleRow = (idx) => setItems(prev => prev.map((it, j) => {
    if (j !== idx) return it;
    const sel = !it._selected;
    return { ...it, _selected: sel, return_qty: sel ? it._max : 0, return_base_qty: sel ? it._max : 0, refund_amount: sel ? it._max * Number(it.sale_price || 0) : 0 };
  }));

  const updateQty = (idx, val) => setItems(prev => prev.map((it, j) => {
    if (j !== idx) return it;
    const q = Math.min(Math.max(0, Number(val) || 0), it._max);
    return { ...it, return_qty: q, return_base_qty: q, refund_amount: q * Number(it.sale_price || 0) };
  }));

  const selected = items.filter(i => i._selected && i.return_qty > 0);
  const totalRefund = selected.reduce((s, i) => s + i.refund_amount, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ background: `linear-gradient(135deg, ${TEAL}, #005f70)`, color: '#fff', py: 1.5, px: 2.5, fontSize: '0.95rem', fontWeight: 700 }}>
        Return Items — {bill?.bill_no}
      </DialogTitle>
      {/* Reason + Refund method — pinned above the scrollable table */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: '1px solid #f0f2f5', flexShrink: 0 }}>
        <TextField autoFocus fullWidth size="small" label="Reason for return" value={reason} onChange={e => setReason(e.target.value)}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#718096', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Refund via
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[
              { key: 'CASH', label: 'Cash' },
              { key: 'CREDIT_NOTE', label: 'Credit Note' },
              { key: 'ONLINE', label: 'Online Transfer' },
            ].map(opt => (
              <Button key={opt.key} size="small"
                onClick={() => setRefundMethod(opt.key)}
                variant={refundMethod === opt.key ? 'contained' : 'outlined'}
                sx={{
                  fontSize: '0.73rem', borderRadius: 2, fontWeight: 600, py: 0.5, px: 1.5,
                  ...(refundMethod === opt.key
                    ? { background: TEAL, color: '#fff !important', borderColor: TEAL, '&:hover': { background: '#005f70', color: '#fff !important' } }
                    : { color: '#4a5568', borderColor: '#c8d0d8', '&:hover': { borderColor: TEAL, color: TEAL } }),
                }}>
                {opt.label}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ p: 0, overflow: 'auto' }}>
        <Box sx={{ overflowX: 'auto', border: '1px solid #c8d0d8', borderRadius: 1, mx: 2.5, my: 1.5 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: '0.78rem' }}>
            <colgroup>
              <col style={{ width: 36 }} />
              <col style={{ width: '32%', minWidth: 140 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 82 }} />
              <col style={{ width: 68 }} />
              <col style={{ width: 88 }} />
              <col style={{ width: 110 }} />
            </colgroup>
            <thead>
              <tr style={{ background: '#eef2f7', position: 'sticky', top: 0, zIndex: 2 }}>
                {[
                  { label: '',            align: 'center' },
                  { label: 'Item',        align: 'left'   },
                  { label: 'Batch',       align: 'left'   },
                  { label: 'Expiry',      align: 'center' },
                  { label: 'Sold',        align: 'center' },
                  { label: 'Return Qty',  align: 'center' },
                  { label: 'Refund (₹)',  align: 'right'  },
                ].map((h, idx) => (
                  <th key={idx} style={{
                    padding: '6px 7px', textAlign: h.align, fontWeight: 700,
                    fontSize: '0.67rem', color: '#4a5568', textTransform: 'uppercase',
                    letterSpacing: '0.4px', borderRight: '1px solid #c8d0d8',
                    borderBottom: '2px solid #aab4c0', whiteSpace: 'nowrap',
                    background: '#eef2f7',
                  }}>{h.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '24px 0', textAlign: 'center', color: '#aaa', fontSize: '0.82rem', background: '#fafbfc' }}>
                    No returnable items
                  </td>
                </tr>
              ) : items.map((it, i) => {
                const isEven = i % 2 === 0;
                const rowBg = it._selected ? '#e8f5e9' : (isEven ? '#ffffff' : '#f7f9fc');
                const cellStyle = {
                  padding: '0', borderRight: '1px solid #dde3ea',
                  borderBottom: '1px solid #dde3ea', verticalAlign: 'middle',
                  background: rowBg,
                };
                const textCell = (content, extra = {}) => (
                  <td style={{ ...cellStyle, padding: '5px 7px', ...extra }}>{content}</td>
                );
                return (
                  <tr key={i}
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleRow(i)}
                    onMouseEnter={e => { if (!it._selected) Array.from(e.currentTarget.cells).forEach(c => { c.style.background = '#fffbea'; }); }}
                    onMouseLeave={e => { if (!it._selected) Array.from(e.currentTarget.cells).forEach(c => { c.style.background = rowBg; }); }}>

                    {/* Checkbox */}
                    <td style={{ ...cellStyle, textAlign: 'center', padding: '4px 2px' }}
                      onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={it._selected} onChange={() => toggleRow(i)}
                        style={{ cursor: 'pointer', width: 14, height: 14, accentColor: TEAL }} />
                    </td>

                    {/* Item */}
                    {textCell(
                      <div style={{ fontWeight: 600, color: '#1a202c', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {it._name}
                      </div>
                    )}

                    {/* Batch */}
                    {textCell(
                      <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#2d3748' }}>{it._batch || '—'}</span>
                    )}

                    {/* Expiry */}
                    {textCell(
                      <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: isExpiredDate(it._exp) ? '#e53e3e' : '#4a5568', display: 'block', textAlign: 'center' }}>
                        {it._exp || '—'}
                      </span>
                    )}

                    {/* Sold qty */}
                    {textCell(
                      <span style={{ display: 'block', textAlign: 'center', fontFamily: 'monospace', fontWeight: 600, color: '#4a5568' }}>{it._max}</span>
                    )}

                    {/* Return qty — editable */}
                    <td style={{ ...cellStyle, padding: 0 }}
                      onClick={e => e.stopPropagation()}>
                      <input
                        type="number" min={0} max={it._max} step={1}
                        value={it.return_qty}
                        disabled={!it._selected}
                        onChange={e => updateQty(i, e.target.value)}
                        style={{
                          width: '100%', height: '100%', minHeight: 34, border: 'none', outline: 'none',
                          background: it._selected ? '#f0fff4' : 'transparent',
                          textAlign: 'center', fontFamily: 'monospace',
                          fontSize: '0.8rem', fontWeight: 700, padding: '4px', boxSizing: 'border-box',
                          color: it._selected ? '#1a202c' : '#bbb',
                          cursor: it._selected ? 'text' : 'not-allowed',
                        }}
                        onFocus={e => { e.target.style.background = '#ebf8ff'; e.target.style.boxShadow = 'inset 0 0 0 2px #4299e1'; }}
                        onBlur={e => { e.target.style.background = it._selected ? '#f0fff4' : 'transparent'; e.target.style.boxShadow = 'none'; }}
                      />
                    </td>

                    {/* Refund amount */}
                    {textCell(
                      <span style={{ display: 'block', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem', color: it._selected && it.refund_amount > 0 ? TEAL : '#bbb' }}>
                        {Number(it.refund_amount).toFixed(2)}
                      </span>
                    )}
                  </tr>
                );
              })}

              {/* Footer total row */}
              {selected.length > 0 && (
                <tr style={{ background: '#f0f4f8', borderTop: '2px solid #aab4c0' }}>
                  <td colSpan={5} style={{ padding: '6px 8px', fontSize: '0.72rem', color: '#718096', fontStyle: 'italic', borderRight: '1px solid #c8d0d8' }}>
                    {selected.length} item{selected.length !== 1 ? 's' : ''} selected for return
                  </td>
                  <td colSpan={2} style={{ padding: '6px 10px', textAlign: 'right' }}>
                    <span style={{ fontSize: '0.72rem', color: '#718096', marginRight: 8 }}>TOTAL REFUND</span>
                    <span style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '0.95rem', color: TEAL }}>₹{totalRefund.toFixed(2)}</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button onClick={onClose} sx={{ fontSize: '0.78rem', color: '#666', '&:hover': { color: '#666', background: 'rgba(0,0,0,0.04)' } }}>Back</Button>
        <Button onClick={() => onConfirm({ reason, refund_method: refundMethod, items: selected.map(({ _name, _batch, _exp, _max, _sub_per_base, _selected, ...rest }) => rest) })}
          variant="contained" disabled={!selected.length}
          sx={{ background: TEAL, '&:hover': { background: '#005f70', color: '#fff !important' }, fontSize: '0.78rem', fontWeight: 700, borderRadius: 2, color: '#fff !important' }}>
          Process Return
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Qty Panel (shown after selecting an item from search) ──

function QtyPanel({ group, maxDiscount, onAdd, onClose, isInterstate = false }) {
  const item = group.item || {};
  const subPerBase = Number(item.sub_units_per_base || 1);
  const isSubSellable = item.is_sub_unit_sellable === 'Y' && !!item.sub_unit;
  const totalBaseStock = group.batches.reduce((s, b) => s + Number(b.qty_base_units), 0);
  const totalSubStock = totalBaseStock * subPerBase;

  // FEFO order — earliest expiry first, pre-selected as the default batch.
  const sortedBatches = [...group.batches].sort((a, b) => (a.expiry_date > b.expiry_date ? 1 : -1));

  const [unitType, setUnitType] = useState('BASE');
  const [qty, setQty] = useState(1);
  const [disc, setDisc] = useState(Number(item.discount_percentage || 0));
  const [selectedBatchId, setSelectedBatchId] = useState(sortedBatches[0]?.id ?? null);
  const [err, setErr] = useState('');

  const selectedBatch = sortedBatches.find(b => b.id === selectedBatchId) || sortedBatches[0];
  const batchBaseStock = Number(selectedBatch?.qty_base_units || 0);
  const batchSubStock = batchBaseStock * subPerBase;
  const maxQty = unitType === 'SUB' ? batchSubStock : batchBaseStock;
  const baseNeeded = unitType === 'SUB' ? qty / subPerBase : qty;
  const unitPrice = Number(selectedBatch?.mrp || 0);

  const handleAdd = () => {
    if (!selectedBatch) { setErr('Select a batch to bill from'); return; }
    if (!qty || qty <= 0) { setErr('Quantity must be > 0'); return; }
    if (baseNeeded > batchBaseStock + 0.0001) { setErr(`Only ${unitType === 'SUB' ? batchSubStock.toFixed(0) : batchBaseStock.toFixed(3)} available in batch ${selectedBatch.batch_no}`); return; }
    if (disc > maxDiscount) { setErr(`Max discount is ${maxDiscount}%`); return; }

    const baseQty = Math.round(baseNeeded * 1000) / 1000;
    const displayQty = unitType === 'SUB' ? Math.round(baseQty * subPerBase * 1000) / 1000 : baseQty;
    onAdd([buildCartItem(selectedBatch, baseQty, unitType, displayQty, disc, 0, isInterstate)]);
  };

  // Keyboard: Enter → add · Esc → close · ↑/↓ (outside the number fields) → change batch
  const onPanelKey = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if (e.key === 'Enter') { e.preventDefault(); handleAdd(); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if ((e.target.tagName || '').toLowerCase() === 'input') return;
      e.preventDefault();
      const curIdx = sortedBatches.findIndex(b => b.id === selectedBatchId);
      const nextIdx = e.key === 'ArrowDown'
        ? Math.min(sortedBatches.length - 1, curIdx + 1)
        : Math.max(0, curIdx - 1);
      const nb = sortedBatches[nextIdx];
      if (nb) { setSelectedBatchId(nb.id); setQty(1); setErr(''); }
    }
  };

  return (
    <Paper elevation={4} onKeyDown={onPanelKey} sx={{ p: 2, borderRadius: 2, border: `1.5px solid ${TEAL}`, background: '#f7fbfc' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>{item.name}</Typography>
          {group.variant && <Typography sx={{ fontSize: '0.72rem', color: '#777' }}>{group.variant.variant_name}</Typography>}
          <Typography sx={{ fontSize: '0.7rem', color: '#888', mt: 0.3 }}>
            Stock: <b style={{ color: totalBaseStock > 0 ? '#2e7d32' : '#c62828' }}>
              {totalBaseStock.toFixed(0)} {item.base_unit?.name || 'units'}
              {isSubSellable ? ` / ${totalSubStock.toFixed(0)} ${item.sub_unit?.name || 'sub'}` : ''}
            </b>
            {' '}· {group.batches.length} batch{group.batches.length !== 1 ? 'es' : ''}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      {/* Batch selection — pick which batch to bill from; each batch can carry its own price */}
      <Box sx={{ mb: 1.5, p: 1, background: '#fff', borderRadius: 1.5, border: '1px solid #e0eff2' }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#555', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.3 }}>
          Select Batch{sortedBatches.length > 1 ? ' (FEFO order)' : ''}
        </Typography>
        {sortedBatches.map((b, i) => {
          const selected = b.id === selectedBatchId;
          return (
            <Box key={b.id} onClick={() => { setSelectedBatchId(b.id); setQty(1); setErr(''); }}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                p: 0.9, mb: i === sortedBatches.length - 1 ? 0 : 0.6, borderRadius: 1.5, cursor: 'pointer',
                border: `1.5px solid ${selected ? TEAL : '#e0e0e0'}`,
                background: selected ? '#e8f5f8' : '#fafafa',
              }}>
              <Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: isExpiredDate(b.expiry_date) ? '#e74c3c' : '#444' }}>
                Batch: <b>{b.batch_no}</b> · Exp: {fmtExpiry(b.expiry_date)} · Qty: {Number(b.qty_base_units).toFixed(0)} {item.base_unit?.name || 'units'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', fontWeight: 700, color: TEAL }}>₹{Number(b.mrp || 0).toFixed(2)}</Typography>
                {selected && <CheckCircleIcon sx={{ color: TEAL, fontSize: 16 }} />}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        {isSubSellable && (
          <ToggleButtonGroup size="small" value={unitType} exclusive onChange={(_, v) => { if (v) { setUnitType(v); setQty(1); } }} sx={{ height: 36 }}>
            <ToggleButton value="BASE" sx={{ fontSize: '0.72rem', px: 1.5, textTransform: 'none' }}>{item.base_unit?.name || 'Base'}</ToggleButton>
            <ToggleButton value="SUB" sx={{ fontSize: '0.72rem', px: 1.5, textTransform: 'none' }}>{item.sub_unit?.name || 'Sub'}</ToggleButton>
          </ToggleButtonGroup>
        )}
        <TextField size="small" label="Quantity" type="number" value={qty} autoFocus
          onChange={e => { setQty(Number(e.target.value) || 0); setErr(''); }}
          inputProps={{ min: 1, max: maxQty, step: unitType === 'SUB' ? 1 : 0.001 }}
          sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.82rem' } }} />
        <Tooltip title={`Max ${maxDiscount}%`} arrow>
          <TextField size="small" label="Discount %" type="number" value={disc}
            onChange={e => { setDisc(Math.min(maxDiscount, Math.max(0, Number(e.target.value) || 0))); setErr(''); }}
            inputProps={{ min: 0, max: maxDiscount, step: 0.5 }}
            sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.82rem' } }} />
        </Tooltip>
        <Typography sx={{ fontSize: '0.72rem', color: '#888', alignSelf: 'center' }}>
          MRP: <b style={{ fontFamily: 'monospace' }}>₹{unitPrice.toFixed(2)}</b>
        </Typography>
        <Tooltip title="Add to Cart" arrow>
          <Button variant="contained" size="small" onClick={handleAdd}
            sx={{ background: TEAL, '&:hover': { background: '#005f70' }, borderRadius: 2, minWidth: 36, width: 36, height: 36, p: 0, color: '#fff !important' }}>
            <AddShoppingCartIcon sx={{ fontSize: 18 }} />
          </Button>
        </Tooltip>
      </Box>

      {/* Live price preview */}
      {qty > 0 && (
        <Box sx={{ mt: 1.5, px: 1.5, py: 1, bgcolor: '#f0f8fa', borderRadius: 1.5, border: `1px solid ${TEAL}33`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#555' }}>
            {qty} × ₹{unitPrice.toFixed(2)}
            {disc > 0 ? ` − ${disc}% disc` : ''}
          </Typography>
          <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: TEAL, fontSize: '0.88rem' }}>
            ₹{(qty * unitPrice * (1 - disc / 100)).toFixed(2)}
          </Typography>
        </Box>
      )}

      {err && <Alert severity="warning" sx={{ mt: 1, fontSize: '0.75rem', py: 0.3 }}>{err}</Alert>}
    </Paper>
  );
}

// ─── Bill print ────────────────────────────────────────────

function amountInWords(amount) {
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  function conv(n) {
    if (n === 0) return '';
    if (n < 20) return ones[n] + ' ';
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '') + ' ';
    return ones[Math.floor(n / 100)] + ' Hundred ' + conv(n % 100);
  }
  const rs = Math.floor(Math.abs(amount));
  const paise = Math.round((Math.abs(amount) - rs) * 100);
  let out = '';
  const cr = Math.floor(rs / 10000000);
  const lk = Math.floor((rs % 10000000) / 100000);
  const th = Math.floor((rs % 100000) / 1000);
  const rem = rs % 1000;
  if (cr) out += conv(cr) + 'Crore ';
  if (lk) out += conv(lk) + 'Lakh ';
  if (th) out += conv(th) + 'Thousand ';
  if (rem) out += conv(rem);
  if (!out.trim()) out = 'Zero ';
  out = 'INR ' + out.trim();
  if (paise) out += ' and ' + conv(paise).trim() + ' Paise';
  return out + ' Only';
}

function gstSummaryRows(items, isInterstate) {
  const map = {};
  for (const it of items) {
    const cgstR = Number(it.cgst_percentage || 0);
    const sgstR = Number(it.sgst_percentage || 0);
    const igstR = Number(it.igst_percentage || 0);
    const key = isInterstate === 'Y' ? `igst_${igstR}` : `cs_${cgstR}_${sgstR}`;
    if (!map[key]) map[key] = { cgstR, sgstR, igstR, taxable: 0, cgst: 0, sgst: 0, igst: 0 };
    map[key].taxable += Number(it.taxable_amount || 0);
    map[key].cgst += Number(it.cgst_amount || 0);
    map[key].sgst += Number(it.sgst_amount || 0);
    map[key].igst += Number(it.igst_amount || 0);
  }
  return Object.values(map).sort((a, b) => (a.cgstR + a.sgstR + a.igstR) - (b.cgstR + b.sgstR + b.igstR));
}

/**
 * Invoice No barcode — { html, initScript }. Deliberately just the
 * invoice's own number, not a patient/couple/spouse barcode strip: whether
 * the bill is for a registered patient or a walk-in/guest, this document
 * only ever needs to scan back to itself.
 */
function invoiceBarcodeParts(bill, idPrefix) {
  return codeBarcodeStack({
    idPrefix,
    items: [{ label: 'Invoice No', code: bill.bill_no, transform: documentCodeValue }],
  });
}

function buildInvoiceHtml(bill, items, totals, pharmacy, isDuplicate) {
  const ph = pharmacy || bill.pharmacy || {};
  const isInterstate = bill.is_interstate === 'Y';
  const paidPayments = (bill.payments || []).filter(p => p.status === 'PAID');
  const paymentMode = paidPayments.length ? [...new Set(paidPayments.map(p => p.payment_mode))].join(' / ') : (bill.payment_mode || '—');

  const PRIMARY = '#005a6e';
  const LIGHT_BG = '#eaf6f8';
  const BORDER = '#b2d8de';

  const PAGE_ROWS = 8;
  const pages = [];
  for (let i = 0; i < Math.max(items.length, 1); i += PAGE_ROWS) pages.push(items.slice(i, i + PAGE_ROWS));

  const unitName = (it) => {
    if (it.unit_type === 'SUB') return it.item?.sub_unit?.name || it.item?.base_unit?.name || 'NOS';
    return it.item?.base_unit?.name || 'NOS';
  };
  const displayQty = (it) => it.unit_type === 'SUB' ? Number(it.qty_sub_units || 0) : Number(it.qty_units || 0);
  const rateExclGst = (it) => {
    const gstPct = Number(it.cgst_percentage || 0) + Number(it.sgst_percentage || 0) + Number(it.igst_percentage || 0);
    return gstPct > 0 ? Number(it.mrp) / (1 + gstPct / 100) : Number(it.mrp);
  };

  const C = `border:1px solid ${BORDER};padding:3px 4px;font-size:7.5pt;`;
  const CH = `${C}background:${LIGHT_BG};color:${PRIMARY};font-weight:700;text-align:center;`;

  const pageHtml = (pg, pi) => {
    const isLast = pi === pages.length - 1;
    const gstSummary = isLast ? gstSummaryRows(items, bill.is_interstate) : null;
    const { html: barcodeHtml, initScript: barcodeInit } = invoiceBarcodeParts(bill, `bc-inv-p${pi}`);

    const headerHtml = `
      <div style="margin-bottom:6px">
        <div style="line-height:0">
          <img src="${irmHeader}" alt="Company Header" style="width:100%;object-fit:contain;display:block">
        </div>
        <div style="text-align:center;margin-top:5px">
          <div style="font-size:14pt;font-weight:700;letter-spacing:1.5px;font-family:'Montserrat',Arial,sans-serif;color:${PRIMARY}">TAX INVOICE${isDuplicate ? ' <span style="font-size:9pt;color:#c0392b;font-weight:700">(DUPLICATE)</span>' : ''}</div>
          ${isInterstate ? `<div style="margin-top:2px"><span style="font-size:7pt;font-weight:700;color:#c0392b;border:1px solid #c0392b;padding:1px 6px;border-radius:2px">INTERSTATE / IGST</span></div>` : ''}
        </div>
      </div>
      <table style="width:100%;border-collapse:collapse;border:1.5px solid ${BORDER};margin-bottom:0">
        <tr>
          <td style="width:50%;border-right:1px solid ${BORDER};padding:5px 8px;vertical-align:top">
            <div style="font-size:12pt;font-weight:900;color:${PRIMARY};line-height:1.1">${ph.name || 'PHARMACY'}</div>
            ${ph.sub_title ? `<div style="font-size:8pt;color:#444;margin-top:1px">${ph.sub_title}</div>` : ''}
            <div style="font-size:7.5pt;color:#333;margin-top:2px;line-height:1.6">${ph.address || ''}${ph.city ? `, ${ph.city}` : ''}${ph.pincode ? ` - ${ph.pincode}` : ''}</div>
            ${ph.dl_no ? `<div style="font-size:7pt;color:#555">DL No : ${ph.dl_no}</div>` : ''}
            ${ph.gstin ? `<div style="font-size:7pt;color:#555">GSTIN : ${ph.gstin}</div>` : ''}
            ${ph.gstin_uin && ph.gstin_uin !== ph.gstin ? `<div style="font-size:7pt;color:#555">GSTIN/UIN : ${ph.gstin_uin}</div>` : ''}
            ${ph.state ? `<div style="font-size:7pt;color:#555">State Name : ${ph.state}${ph.state_code ? `, Code : ${ph.state_code}` : ''}</div>` : ''}
            ${ph.email ? `<div style="font-size:7pt;color:#555">E-Mail : ${ph.email}</div>` : ''}
            ${ph.mobile ? `<div style="font-size:7pt;color:#555">Mobile : ${ph.mobile}</div>` : ''}
          </td>
          <td style="width:50%;padding:5px 8px;vertical-align:top">
            <table style="width:100%;border-collapse:collapse;font-size:7.5pt">
              <tr><td style="color:#777;padding-bottom:3px;padding-right:6px;white-space:nowrap">Invoice No.</td><td style="font-weight:700;padding-bottom:3px">${bill.bill_no}</td></tr>
              <tr><td style="color:#777;padding-bottom:3px;padding-right:6px;white-space:nowrap">Dated</td><td style="font-weight:700;padding-bottom:3px">${fmtDate(bill.bill_date)}</td></tr>
              <tr><td style="color:#777;padding-bottom:3px;padding-right:6px;white-space:nowrap">Mode / Terms of Payment</td><td style="font-weight:700;padding-bottom:3px">${paymentMode}</td></tr>
              <tr><td style="color:#777;padding-right:6px;white-space:nowrap">Reference No. &amp; Date</td><td></td></tr>
            </table>
            ${barcodeHtml ? `<div style="margin-top:4px;display:flex;justify-content:flex-end">${barcodeHtml}</div>` : ''}
          </td>
        </tr>
        <tr style="border-top:1px solid ${BORDER}">
          <td style="width:50%;border-right:1px solid ${BORDER};padding:5px 8px;vertical-align:top">
            <div style="font-size:7pt;color:${PRIMARY};font-weight:700;text-transform:uppercase;margin-bottom:2px">Patient / Consignee</div>
            <div style="font-weight:700;font-size:9.5pt">${bill.patient_name}</div>
            <div style="font-size:7.5pt;color:#555">${[bill.patient_age ? `Age: ${bill.patient_age}` : '', bill.patient_gender || ''].filter(Boolean).join(' | ')}</div>
            ${bill.patient_mobile ? `<div style="font-size:7.5pt;color:#555">Mob: ${bill.patient_mobile}</div>` : ''}
            ${bill.patient_address ? `<div style="font-size:7pt;color:#777">${bill.patient_address}</div>` : ''}
            <div style="font-size:7pt;color:#555;margin-top:2px">State : ${ph.state || '—'}${ph.state_code ? `, Code : ${ph.state_code}` : ''}</div>
          </td>
          <td style="width:50%;padding:5px 8px;vertical-align:top">
            <table style="width:100%;border-collapse:collapse;font-size:7.5pt">
              <tr>
                <td style="color:#777;padding-bottom:3px;padding-right:6px;white-space:nowrap">Doctor</td>
                <td style="font-weight:600;padding-bottom:3px">${bill.is_self === 'Y' ? 'Self' : (bill.doctor?.name || bill.doctor_name || '—')}</td>
              </tr>
              <tr>
                <td style="color:#777;padding-right:6px;white-space:nowrap">Mode of Payment</td>
                <td style="font-weight:600">${paymentMode}</td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
      ${barcodeScriptBlock(barcodeInit)}`;

    const itemCards = pg.map((it, i) => {
      const rateExcl = rateExclGst(it);
      const qty = displayQty(it);
      const unit = unitName(it);
      const disc = Number(it.discount_percentage);
      const gstLabel = isInterstate
        ? `IGST ${Number(it.igst_percentage).toFixed(1)}%: <b style="font-family:monospace">₹${Number(it.igst_amount).toFixed(2)}</b>`
        : `CGST ${Number(it.cgst_percentage).toFixed(1)}% + SGST ${Number(it.sgst_percentage).toFixed(1)}%: <b style="font-family:monospace">₹${(Number(it.cgst_amount) + Number(it.sgst_amount)).toFixed(2)}</b>`;
      return `
      <div style="border:1px solid ${BORDER};border-radius:3px;margin-bottom:3px;padding:4px 6px;${i % 2 !== 0 ? `background:${LIGHT_BG}66` : ''}">
        <div style="display:flex;justify-content:space-between;align-items:baseline;gap:6px">
          <div style="flex:1;min-width:0;font-size:7.8pt">
            <span style="font-weight:700;color:${PRIMARY}">${i + 1 + (pi * PAGE_ROWS)}.</span>
            <span style="font-weight:700">${it.item_name}</span>${it.variant_name ? ` <span style="font-weight:400;color:#666;font-size:7pt">(${it.variant_name})</span>` : ''}
          </div>
          <div style="font-weight:800;font-family:monospace;font-size:8.3pt;color:${PRIMARY};white-space:nowrap">₹${Number(it.line_total).toFixed(2)}</div>
        </div>
        <div style="font-size:6.3pt;color:#888;font-family:monospace;margin-top:1px">Batch ${it.batch_no || '—'} · Exp ${fmtExpiry(it.expiry_date)} · HSN ${it.hsn_code || '—'}</div>
        <div style="display:flex;flex-wrap:wrap;column-gap:10px;row-gap:1px;font-size:6.7pt;color:#555;margin-top:3px;border-top:1px dashed ${BORDER};padding-top:2px">
          <span>Qty <b style="font-family:monospace">${qty.toFixed(2)} ${unit}</b></span>
          <span>MRP <b style="font-family:monospace">₹${Number(it.mrp).toFixed(2)}</b></span>
          <span>Rate (ex-tax) <b style="font-family:monospace">₹${rateExcl.toFixed(2)}</b></span>
          ${disc > 0 ? `<span>Disc <b style="font-family:monospace">${disc.toFixed(1)}%</b></span>` : ''}
          <span>Taxable <b style="font-family:monospace">₹${Number(it.taxable_amount).toFixed(2)}</b></span>
          <span>${gstLabel}</span>
        </div>
      </div>`;
    }).join('');

    // net_amount is already round-off-adjusted when enabled; round_off_amount
    // carries the signed delta (0 when the option is off).
    const roundedOff = isLast ? Number(totals.round_off_amount || 0) : 0;
    const finalAmount = isLast ? Number(totals.net_amount) : 0;
    const totalTax = totals.total_cgst + totals.total_sgst + totals.total_igst;

    const pageTotalsHtml = isLast ? `
      <table style="width:100%;border-collapse:collapse;margin-top:2px;border:1px solid ${BORDER}">
        ${Math.abs(roundedOff) >= 0.005 ? `
        <tr>
          <td style="${C}font-style:italic;color:#777">Rounded Off</td>
          <td style="${C}text-align:right;font-family:monospace;color:#777;width:90px">${roundedOff >= 0 ? '+' : ''}${roundedOff.toFixed(2)}</td>
        </tr>` : ''}
        <tr style="background:${LIGHT_BG}">
          <td style="${C}font-weight:800;font-size:8pt">Page Total</td>
          <td style="${C}text-align:right;font-family:monospace;font-weight:800;font-size:8.5pt;width:90px">₹${finalAmount.toFixed(2)}</td>
        </tr>
      </table>` : '';

    const tableHtml = `
      <div style="border-top:1.5px solid ${PRIMARY};padding-top:4px;margin-top:2px">
        ${itemCards}
      </div>
      ${pageTotalsHtml}`;

    const gstSummaryHtml = gstSummary ? (() => {
      const rows = gstSummary.map(g => isInterstate
        ? `<tr>
             <td style="${C}text-align:right;font-family:monospace">${g.taxable.toFixed(2)}</td>
             <td style="${C}text-align:center">—</td><td style="${C}text-align:right">—</td>
             <td style="${C}text-align:center">—</td><td style="${C}text-align:right">—</td>
             <td style="${C}text-align:center">${g.igstR.toFixed(2)}%</td>
             <td style="${C}text-align:right;font-family:monospace">${g.igst.toFixed(2)}</td>
             <td style="${C}text-align:right;font-family:monospace;font-weight:700">${(g.taxable + g.igst).toFixed(2)}</td>
           </tr>`
        : `<tr>
             <td style="${C}text-align:right;font-family:monospace">${g.taxable.toFixed(2)}</td>
             <td style="${C}text-align:center">${g.cgstR.toFixed(2)}%</td>
             <td style="${C}text-align:right;font-family:monospace">${g.cgst.toFixed(2)}</td>
             <td style="${C}text-align:center">${g.sgstR.toFixed(2)}%</td>
             <td style="${C}text-align:right;font-family:monospace">${g.sgst.toFixed(2)}</td>
             <td style="${C}text-align:right;font-family:monospace;font-weight:700">${(g.taxable + g.cgst + g.sgst).toFixed(2)}</td>
           </tr>`
      ).join('');
      return `
        <table style="width:100%;border-collapse:collapse;font-size:7pt;table-layout:fixed">
          <tr>
            <th style="${CH}">Taxable Value</th>
            <th style="${CH}">CGST Rate</th><th style="${CH}">CGST Amt</th>
            <th style="${CH}">SGST/UTGST Rate</th><th style="${CH}">SGST/UTGST Amt</th>
            ${isInterstate ? `<th style="${CH}">IGST Rate</th><th style="${CH}">IGST Amt</th>` : ''}
            <th style="${CH}">Total Tax Amt</th>
          </tr>
          ${rows}
          <tr style="background:${LIGHT_BG};font-weight:700">
            <td style="${C}text-align:right;font-family:monospace">${totals.taxable_amount.toFixed(2)}</td>
            <td style="${C}text-align:center">—</td>
            <td style="${C}text-align:right;font-family:monospace">${isInterstate ? '—' : totals.total_cgst.toFixed(2)}</td>
            <td style="${C}text-align:center">—</td>
            <td style="${C}text-align:right;font-family:monospace">${isInterstate ? '—' : totals.total_sgst.toFixed(2)}</td>
            ${isInterstate ? `<td style="${C}text-align:center">—</td><td style="${C}text-align:right;font-family:monospace">${totals.total_igst.toFixed(2)}</td>` : ''}
            <td style="${C}text-align:right;font-family:monospace;font-weight:700">${totalTax.toFixed(2)}</td>
          </tr>
        </table>`;
    })() : '';

    const lastPageFooter = isLast ? `
      <table style="width:100%;border-collapse:collapse;margin-top:0;font-size:7.5pt;border:1px solid ${BORDER};border-top:none">
        <tr>
          <td style="padding:4px 7px;border-right:1px solid ${BORDER};font-weight:700;color:#555;white-space:nowrap;font-size:7pt">Amount Chargeable (in words)</td>
          <td style="padding:4px 10px;font-style:italic;font-size:8pt">${amountInWords(finalAmount)}</td>
          <td style="padding:4px 7px;border-left:1px solid ${BORDER};font-size:7pt;color:#888;text-align:right;white-space:nowrap">E. &amp; O.E.</td>
        </tr>
      </table>

      <div style="display:flex;justify-content:space-between;gap:10px;margin-top:5px">
        <div style="width:34%">
          <div style="font-size:7pt;font-weight:700;color:${PRIMARY};text-transform:uppercase;letter-spacing:0.5px;margin-bottom:2px">Bill Summary</div>
          <table style="width:100%;border-collapse:collapse;border:1px solid ${BORDER};font-size:7.5pt">
            <tr>
              <td style="padding:3px 7px;color:#555">Sub Total</td>
              <td style="padding:3px 7px;text-align:right;font-family:monospace">${totals.sub_total.toFixed(2)}</td>
            </tr>
            ${totals.total_discount > 0 ? `
            <tr style="border-top:1px solid ${BORDER}">
              <td style="padding:3px 7px;color:#e53e3e">Total Discount</td>
              <td style="padding:3px 7px;text-align:right;font-family:monospace;color:#e53e3e">&minus;${totals.total_discount.toFixed(2)}</td>
            </tr>` : ''}
            ${Math.abs(roundedOff) >= 0.005 ? `
            <tr style="border-top:1px solid ${BORDER}">
              <td style="padding:3px 7px;color:#888">Round Off</td>
              <td style="padding:3px 7px;text-align:right;font-family:monospace;color:#888">${roundedOff >= 0 ? '+' : '&minus;'}${Math.abs(roundedOff).toFixed(2)}</td>
            </tr>` : ''}
            <tr style="border-top:1px solid ${BORDER};background:${LIGHT_BG}">
              <td style="padding:4px 7px;font-weight:800;font-size:8pt">Grand Total</td>
              <td style="padding:4px 7px;text-align:right;font-family:monospace;font-weight:800;font-size:8.5pt">${finalAmount.toFixed(2)}</td>
            </tr>
          </table>
        </div>
        <div style="width:62%">
          <div style="font-size:7pt;font-weight:700;color:${PRIMARY};text-transform:uppercase;letter-spacing:0.5px;margin-bottom:2px">Tax Analysis</div>
          ${gstSummaryHtml}
          ${totalTax > 0 ? `<div style="margin-top:3px;font-size:7.5pt;font-style:italic"><b>Tax Amount (in words):</b> ${amountInWords(totalTax)}</div>` : ''}
        </div>
      </div>

      <table style="width:100%;border-collapse:collapse;margin-top:5px;border:1px solid ${BORDER};font-size:7.5pt">
        <tr>
          <td style="width:45%;vertical-align:top;border-right:1px solid ${BORDER};padding:5px 7px">
            ${ph.pan_no ? `<div style="margin-bottom:3px"><b>Company&apos;s PAN:</b> <span style="font-family:monospace;letter-spacing:0.8px">${ph.pan_no}</span></div>` : ''}
            ${ph.declaration ? `
              <div style="font-size:6.5pt;font-weight:700;color:${PRIMARY};text-transform:uppercase;margin-top:${ph.pan_no ? '4px' : '0'};margin-bottom:2px">Declaration</div>
              <div style="font-size:6.5pt;color:#555;line-height:1.5">${ph.declaration}</div>
            ` : ''}
          </td>
          <td style="width:30%;vertical-align:top;border-right:1px solid ${BORDER};padding:5px 7px">
            ${ph.bank_name ? `
              <div style="font-size:6.5pt;font-weight:700;color:${PRIMARY};text-transform:uppercase;margin-bottom:3px">Company&apos;s Bank Details</div>
              <table style="font-size:7pt;border-collapse:collapse;width:100%">
                <tr><td style="color:#777;padding-bottom:2px;white-space:nowrap;padding-right:4px">Bank Name</td><td style="font-weight:600;padding-bottom:2px">: ${ph.bank_name}</td></tr>
                ${ph.account_no ? `<tr><td style="color:#777;padding-bottom:2px;white-space:nowrap;padding-right:4px">A/c No.</td><td style="font-weight:600;padding-bottom:2px">: ${ph.account_no}</td></tr>` : ''}
                ${ph.bank_branch ? `<tr><td style="color:#777;padding-bottom:2px;white-space:nowrap;padding-right:4px">Branch</td><td style="font-weight:600;padding-bottom:2px">: ${ph.bank_branch}</td></tr>` : ''}
                ${ph.ifsc_code ? `<tr><td style="color:#777;padding-bottom:2px;white-space:nowrap;padding-right:4px">IFS Code</td><td style="font-weight:600;padding-bottom:2px">: ${ph.ifsc_code}</td></tr>` : ''}
              </table>
            ` : ''}
          </td>
          <td style="width:25%;vertical-align:bottom;text-align:center;padding:5px 7px;padding-top:18px">
            <div style="font-size:7.5pt;font-weight:700;color:#333;margin-bottom:18px">for ${ph.name || ''}</div>
            <div style="border-top:1.5px solid #333;padding-top:4px;font-size:8pt;font-weight:700">Authorised Signatory</div>
          </td>
        </tr>
        <tr style="border-top:1px solid ${BORDER}">
          <td colspan="2" style="padding:5px 7px;border-right:1px solid ${BORDER};vertical-align:middle">
            <div style="height:36px;border:1px dashed #ccc;border-radius:3px;display:flex;align-items:center;justify-content:center;color:#ccc;font-style:italic;font-size:7pt">Authorised Seal</div>
          </td>
          <td style="padding:5px 7px;text-align:center;vertical-align:middle;font-size:6.5pt;color:#aaa;font-style:italic">
            This is a Computer Generated Invoice
          </td>
        </tr>
      </table>` : '';

    const pageFooter = `
      <div style="margin-top:4px;display:flex;justify-content:flex-end;align-items:center;font-size:7pt;color:#888;border-top:1px solid ${BORDER};padding-top:3px">
        <span>${isLast ? `Page ${pi + 1} of ${pages.length}` : `continued to page ${pi + 2} →`}</span>
      </div>`;

    return `
      <div class="pf-page" style="page-break-after:${isLast ? 'auto' : 'always'};padding:6px;font-family:Arial,sans-serif;width:499px;margin:0 auto">
        ${headerHtml}
        ${tableHtml}
        ${lastPageFooter}
        ${pageFooter}
      </div>`;
  };

  return pages.map((pg, pi) => pageHtml(pg, pi)).join('');
}

function printBill(bill) {
  if (!bill) return;
  const items = (bill.items || []).map(calcLineTotals);
  const totals = calcBillTotals(items, bill.round_off_enabled !== 'N');
  const bodyHtml = buildInvoiceHtml(bill, items, totals, bill.pharmacy, false);
  const win = window.open('', '_blank', 'width=960,height=720');
  const toolbar = `<div id="ptb" style="position:fixed;top:0;left:0;right:0;background:#005a6e;color:#fff;padding:9px 16px;display:flex;justify-content:space-between;align-items:center;z-index:9999;font-family:Arial,sans-serif;box-shadow:0 2px 8px rgba(0,0,0,0.25)"><span style="font-weight:700;font-size:10.5pt">Tax Invoice &mdash; ${bill.bill_no}</span><div style="display:flex;gap:8px"><button onclick="window.print()" style="background:#fff;color:#005a6e;border:none;padding:7px 16px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#128438; Print</button><button onclick="window.print()" style="background:rgba(255,255,255,0.18);color:#fff;border:1px solid rgba(255,255,255,0.5);padding:7px 16px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#8595; Download PDF</button><button onclick="window.close()" style="background:transparent;color:rgba(255,255,255,0.8);border:1px solid rgba(255,255,255,0.4);padding:7px 10px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#10005;</button></div></div>`;
  win.document.write(`<!DOCTYPE html><html><head><title>Tax Invoice ${bill.bill_no}</title>
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&display=swap">
    <style>@page{size:A5 portrait;margin:8mm}*{box-sizing:border-box}body{font-family:'Montserrat',Arial,sans-serif;margin:0;padding:50px 0 0 0;-webkit-print-color-adjust:exact;print-color-adjust:exact}td,th{overflow-wrap:break-word}@media print{#ptb{display:none!important}body{padding-top:0!important}}</style>
    </head><body>${toolbar}${bodyHtml}
    <script>window.onload=function(){try{var els=document.querySelectorAll('.pf-page');for(var i=0;i<els.length;i++){var h=els[i].scrollHeight;if(h>733)els[i].style.zoom=Math.max(0.55,733/h);}}catch(e){}};</script>
    </body></html>`);
  win.document.close();
}

// ─── Duplicate bill print (current/remaining quantities) ───

function printDuplicateBill(bill) {
  if (!bill) return;
  const items = (bill.items || []).map(it => {
    const returned = Number(it.returned_qty || 0);
    const remaining = Number(it.qty_base_deducted) - returned;
    if (remaining <= 0) return null;
    return calcLineTotals({
      ...it,
      qty_units: it.unit_type === 'BASE' ? remaining : it.qty_units,
      qty_sub_units: it.unit_type === 'SUB' ? remaining : it.qty_sub_units,
      qty_base_deducted: remaining,
    });
  }).filter(Boolean);

  if (!items.length) { window.alert('No items with remaining quantity to print.'); return; }

  const totals = calcBillTotals(items, bill.round_off_enabled !== 'N');
  const bodyHtml = buildInvoiceHtml(bill, items, totals, bill.pharmacy, true);
  const win = window.open('', '_blank', 'width=960,height=720');
  const toolbar = `<div id="ptb" style="position:fixed;top:0;left:0;right:0;background:#005a6e;color:#fff;padding:9px 16px;display:flex;justify-content:space-between;align-items:center;z-index:9999;font-family:Arial,sans-serif;box-shadow:0 2px 8px rgba(0,0,0,0.25)"><span style="font-weight:700;font-size:10.5pt">Tax Invoice (Duplicate) &mdash; ${bill.bill_no}</span><div style="display:flex;gap:8px"><button onclick="window.print()" style="background:#fff;color:#005a6e;border:none;padding:7px 16px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#128438; Print</button><button onclick="window.print()" style="background:rgba(255,255,255,0.18);color:#fff;border:1px solid rgba(255,255,255,0.5);padding:7px 16px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#8595; Download PDF</button><button onclick="window.close()" style="background:transparent;color:rgba(255,255,255,0.8);border:1px solid rgba(255,255,255,0.4);padding:7px 10px;border-radius:4px;font-weight:700;cursor:pointer;font-size:9pt">&#10005;</button></div></div>`;
  win.document.write(`<!DOCTYPE html><html><head><title>Tax Invoice ${bill.bill_no} (Duplicate)</title>
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&display=swap">
    <style>@page{size:A5 portrait;margin:8mm}*{box-sizing:border-box}body{font-family:'Montserrat',Arial,sans-serif;margin:0;padding:50px 0 0 0;-webkit-print-color-adjust:exact;print-color-adjust:exact}td,th{overflow-wrap:break-word}@media print{#ptb{display:none!important}body{padding-top:0!important}}</style>
    </head><body>${toolbar}${bodyHtml}
    <script>window.onload=function(){try{var els=document.querySelectorAll('.pf-page');for(var i=0;i<els.length;i++){var h=els[i].scrollHeight;if(h>733)els[i].style.zoom=Math.max(0.55,733/h);}}catch(e){}};</script>
    </body></html>`);
  win.document.close();
}

// ─── Main Form ─────────────────────────────────────────────

export default function BillingForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const { selectedPharmacy } = usePharmacy();

  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [maxDiscount, setMaxDiscount] = useState(25);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState(null);

  // Patient
  const [patientSearch, setPatientSearch] = useState('');
  const [patientSearching, setPatientSearching] = useState(false);
  const [patientResults, setPatientResults] = useState([]);
  const [patientMode, setPatientMode] = useState('search');
  const [patient, setPatient] = useState({ patient_name: '', patient_mobile: '', patient_gender: '', patient_age: '', patient_address: '', irm_contacts_id: null, irm_pharm_guest_patients_id: null });
  const EMPTY_PATIENT = { patient_name: '', patient_mobile: '', patient_gender: '', patient_age: '', patient_address: '', irm_contacts_id: null, irm_pharm_guest_patients_id: null };

  // Doctor
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [isSelf, setIsSelf] = useState(false);
  const [addingDoctor, setAddingDoctor] = useState(false);
  const [newDoctorName, setNewDoctorName] = useState('');
  const [savingDoctor, setSavingDoctor] = useState(false);

  // Tax type
  const [isInterstate, setIsInterstate] = useState(false);
  // Round net amount to the nearest rupee (per-bill, default on)
  const [roundOffEnabled, setRoundOffEnabled] = useState(true);

  // Item search — grouped by item
  const [itemSearch, setItemSearch] = useState('');
  const [itemSearching, setItemSearching] = useState(false);
  const [itemGroups, setItemGroups] = useState([]); // [{ key, item, variant, batches }]
  const [selectedGroup, setSelectedGroup] = useState(null); // item selected, showing QtyPanel
  const itemSearchTimer = useRef(null);
  const patientSearchTimer = useRef(null);
  const searchBoxRef = useRef(null);
  const itemResultRefs = useRef([]);
  const doctorInputRef = useRef(null);

  // Cart
  const [cartItems, setCartItems] = useState([]);

  // Dialogs
  const [payDialog, setPayDialog] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [returnDialog, setReturnDialog] = useState(false);
  const [creditNotes, setCreditNotes] = useState([]);
  const [lastReturn, setLastReturn] = useState(null);
  const [debitNoteCn, setDebitNoteCn] = useState(null); // CN object to issue DN against
  const [showTimeline, setShowTimeline] = useState(false);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  // Load config, locations and doctors
  useEffect(() => {
    api.pharmacy.getBillingConfig('max_bill_discount').then(r => { if (r?.value) setMaxDiscount(+r.value); }).catch(() => {});
    api.pharmacy.listDoctors().then(r => setDoctorOptions(r || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const params = selectedPharmacy ? { pharmacy_id: selectedPharmacy.id } : {};
    api.pharmacy.listLocations(params).then(r => {
      const locs = r?.data || (Array.isArray(r) ? r : []);
      setLocations(locs);
      // auto-select if only one or if none selected yet
      if (locs.length === 1 && !locationId) setLocationId(locs[0].id);
    }).catch(() => {});
  }, [selectedPharmacy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load existing bill
  useEffect(() => {
    if (!isNew) {
      setLoading(true);
      api.pharmacy.getBill(id).then(b => {
        setBill(b);
        setPatient({ patient_name: b.patient_name, patient_mobile: b.patient_mobile || '', patient_gender: b.patient_gender || '', patient_age: b.patient_age || '', patient_address: b.patient_address || '', irm_contacts_id: b.irm_contacts_id, irm_pharm_guest_patients_id: b.irm_pharm_guest_patients_id || null });
        setPatientMode(b.irm_contacts_id || b.irm_pharm_guest_patients_id ? 'found' : 'manual');
        if (b.is_self === 'Y') setIsSelf(true);
        else if (b.irm_pharm_doctors_id) setSelectedDoctor({ id: b.irm_pharm_doctors_id, name: b.doctor?.name || b.doctor_name || '' });
        setIsInterstate(b.is_interstate === 'Y');
        setRoundOffEnabled(b.round_off_enabled !== 'N');
        // apply calcLineTotals to ensure computed fields match current GST/discount state
        setCartItems((b.items || []).map(it => calcLineTotals({ ...it, _key: `existing_${it.id}`, _base_unit: '', _sub_unit: null, _sub_per_base: 1, _is_sub_sellable: false, _max_stock: 999 })));
        setLocationId(b.irm_pharm_store_locations_id);
      }).catch(() => setError('Failed to load bill'))
        .finally(() => setLoading(false));
    }
  }, [id, isNew]);

  // Recompute all cart taxes when interstate flag toggles
  useEffect(() => {
    if (!cartItems.length) return;
    setCartItems(prev => prev.map(it => {
      const gstExCess = Number(it.gst_percentage || 0) - Number(it.cess_percentage || 0);
      const cgstPct = isInterstate ? 0 : gstExCess / 2;
      const sgstPct = isInterstate ? 0 : gstExCess / 2;
      const igstPct = isInterstate ? gstExCess : 0;
      return calcLineTotals({ ...it, cgst_percentage: cgstPct, sgst_percentage: sgstPct, igst_percentage: igstPct });
    }));
  }, [isInterstate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Patient search — debounced, fires as user types
  const handlePatientInputChange = (_, inputVal) => {
    setPatientSearch(inputVal);
    clearTimeout(patientSearchTimer.current);
    if (!inputVal || inputVal.length < 2) { setPatientResults([]); return; }
    setPatientSearching(true);
    patientSearchTimer.current = setTimeout(async () => {
      try {
        const res = await api.pharmacy.searchBillingPatients(inputVal, 10);
        // response shape: { data: [...], total_count: N } OR plain array (fallback)
        const arr = Array.isArray(res) ? res : (res?.data || []);
        setPatientResults(arr);
      } catch { setPatientResults([]); }
      finally { setPatientSearching(false); }
    }, 300);
  };

  const calcAgeFromDob = (dob) => {
    if (!dob) return '';
    const diff = Date.now() - new Date(dob).getTime();
    return Math.floor(diff / (365.25 * 24 * 3600 * 1000));
  };

  const selectPatient = (p) => {
    if (!p) return;
    const isGuest = !!p.is_guest;
    const cid = !isGuest ? (p.id || null) : null;
    const fullName = p.full_name?.trim() || [p.first_name, p.middle_name, p.last_name].filter(Boolean).join(' ') || '';
    const mobile = p.mobile_number || p.phones?.[0]?.phone_number || '';
    const age = p.date_of_birth ? calcAgeFromDob(p.date_of_birth) : (p.age ?? '');
    // gender: contact uses M/F/O/U; billing form uses M/F/O — map U → ''
    const genderMap = { M: 'M', F: 'F', O: 'O', U: '' };
    const gender = genderMap[p.gender] ?? '';
    const address = p.address || '';
    setPatient({ patient_name: fullName, patient_mobile: mobile, patient_gender: gender, patient_age: String(age), patient_address: address, irm_contacts_id: cid, irm_pharm_guest_patients_id: isGuest ? p.id : null });
    setPatientMode('found');
    setPatientResults([]);
    setPatientSearch('');
    // Tally flow: jump to the doctor field once a patient is picked
    setTimeout(() => { (isSelf ? searchBoxRef : doctorInputRef).current?.focus(); }, 60);
    if (cid) api.pharmacy.listCreditNotes(cid).then(r => setCreditNotes(r || [])).catch(() => {});
  };

  // Item search — group batches by item+variant
  const executeItemSearch = async (val) => {
    if (!val || val.length < 2) return;
    if (!locationId) { setError('Please select a store location before searching items.'); return; }
    setItemSearching(true);
    try {
      const res = await api.pharmacy.searchBillingItems(val, locationId);
      const batches = res || [];
      const map = new Map();
      for (const inv of batches) {
        const k = `${inv.irm_pharm_items_id}_${inv.irm_pharm_item_variants_id || 0}`;
        if (!map.has(k)) map.set(k, { key: k, item: inv.item || {}, variant: inv.variant || null, batches: [] });
        map.get(k).batches.push(inv);
      }
      setItemGroups(Array.from(map.values()));
    } catch { setItemGroups([]); }
    finally { setItemSearching(false); }
  };

  const handleItemSearch = (val) => {
    setItemSearch(val);
    setSelectedGroup(null);
    clearTimeout(itemSearchTimer.current);
    if (val.length < 2) { setItemGroups([]); return; }
    // 6+ chars: auto-search with debounce (handles barcode scans)
    // 2-5 chars: only search on Enter — no auto-trigger
    if (val.length >= 6) {
      itemSearchTimer.current = setTimeout(() => executeItemSearch(val), 300);
    }
  };

  const handleItemSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      clearTimeout(itemSearchTimer.current);
      if (itemSearch.length >= 2) executeItemSearch(itemSearch);
    } else if (e.key === 'ArrowDown' && itemGroups.length > 0) {
      e.preventDefault();
      itemResultRefs.current[0]?.focus();
    }
  };

  // Keyboard nav within the item-search results dropdown.
  const itemResultKeyDown = (idx) => (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      (itemResultRefs.current[idx + 1] || itemResultRefs.current[0])?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (idx === 0) searchBoxRef.current?.focus();
      else itemResultRefs.current[idx - 1]?.focus();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (itemGroups[idx]) selectItemGroup(itemGroups[idx]);
    } else if (e.key === 'Escape') {
      setItemGroups([]);
      searchBoxRef.current?.focus();
    }
  };

  const selectItemGroup = (group) => {
    setSelectedGroup(group);
    setItemGroups([]);
    setItemSearch('');
  };

  const handleAddCartRows = (rows) => {
    setCartItems(prev => {
      const base = prev.length;
      return [...prev, ...rows.map((r, i) => ({ ...r, sort_order: base + i + 1, _key: r._key || `${r.irm_pharm_inventory_id}_${Date.now()}_${i}` }))];
    });
    setSelectedGroup(null);
    searchBoxRef.current?.focus();
  };

  const removeCartItem = (key) => setCartItems(prev => prev.filter(i => i._key !== key));

  const updateCartDiscount = (key, val) => {
    setCartItems(prev => prev.map(it => {
      if (it._key !== key) return it;
      const disc = Math.min(maxDiscount, Math.max(0, Number(val) || 0));
      return calcLineTotals({ ...it, discount_percentage: disc });
    }));
  };

  const updateCartMrp = (key, val) => {
    setCartItems(prev => prev.map(it => {
      if (it._key !== key) return it;
      const mrp = Math.max(0, Number(val) || 0);
      return calcLineTotals({ ...it, mrp });
    }));
  };

  const updateCartQty = (key, field, val) => {
    setCartItems(prev => prev.map(it => {
      if (it._key !== key) return it;
      const n = Math.max(0, Number(val) || 0);
      let updated = { ...it };
      if (field === 'qty_units') { updated.qty_units = n; updated.qty_base_deducted = n; }
      if (field === 'qty_sub_units') { updated.qty_sub_units = n; updated.qty_base_deducted = n / (it._sub_per_base || 1); }
      return calcLineTotals(updated);
    }));
  };

  const openTimeline = async () => {
    setShowTimeline(true);
    setTimelineLoading(true);
    try {
      const res = await api.pharmacy.getBillingTimeline(id);
      setTimelineEvents(Array.isArray(res) ? res : (res?.data || []));
    } catch { setTimelineEvents([]); }
    finally { setTimelineLoading(false); }
  };

  const isReadOnly = bill && bill.status !== 'DRAFT';

  // Always compute from live cart (DRAFT = full; read-only = effective remaining after returns)
  const effectiveCartItems = isReadOnly
    ? cartItems.map(it => {
        const remaining = Number(it.qty_base_deducted) - Number(it.returned_qty || 0);
        if (remaining <= 0) return null;
        return calcLineTotals({ ...it, qty_base_deducted: remaining, qty_units: remaining, qty_sub_units: remaining * Number(it._sub_per_base || 1) });
      }).filter(Boolean)
    : cartItems;
  const totals = calcBillTotals(isReadOnly ? effectiveCartItems : cartItems, roundOffEnabled);
  const originalTotals = isReadOnly ? calcBillTotals(cartItems, roundOffEnabled) : null;
  const effectiveById = isReadOnly ? Object.fromEntries(effectiveCartItems.map(e => [String(e.id), e])) : {};

  const buildDto = () => ({
    irm_pharm_pharmacies_id: selectedPharmacy?.id ?? null,
    irm_pharm_store_locations_id: locationId,
    irm_contacts_id: patient.irm_contacts_id || null,
    irm_pharm_guest_patients_id: patient.irm_pharm_guest_patients_id || null,
    patient_name: patient.patient_name,
    patient_mobile: patient.patient_mobile || null,
    patient_gender: patient.patient_gender || null,
    patient_age: patient.patient_age ? +patient.patient_age : null,
    patient_address: patient.patient_address || null,
    irm_pharm_doctors_id: isSelf ? null : (selectedDoctor?.id || null),
    doctor_name: isSelf ? null : (selectedDoctor?.name || null),
    is_self: isSelf ? 'Y' : 'N',
    is_interstate: isInterstate ? 'Y' : 'N',
    round_off_enabled: roundOffEnabled ? 'Y' : 'N',
    items: cartItems.map(({ _key, _base_unit, _sub_unit, _sub_per_base, _is_sub_sellable, _max_stock, ...rest }) => rest),
  });

  const validate = () => {
    if (!patient.patient_name) return 'Patient name is required';
    if (!isSelf && !selectedDoctor) return 'Select a doctor or mark as Self';
    if (!cartItems.length) return 'Add at least one item';
    return null;
  };

  const handleSaveDraft = async () => {
    const vErr = validate();
    if (vErr) { setError(vErr); return; }
    setSaving(true); setError('');
    try {
      let res;
      if (isNew) res = await api.pharmacy.createBill(buildDto());
      else res = await api.pharmacy.updateBill(id, buildDto());
      setBill(res);
      if (isNew) navigate(`/pharmacy/billing/${res.id}`, { replace: true });
    } catch (e) { setError(e?.response?.data?.message || e?.message || 'Failed to save bill'); }
    finally { setSaving(false); }
  };

  const handleFinalize = async ({ payments: pmts, credit_note_ids }) => {
    setSaving(true); setError('');
    setPayDialog(false);
    try {
      let billId = id;
      if (isNew || bill?.status === 'DRAFT') {
        const vErr = validate();
        if (vErr) { setError(vErr); setSaving(false); return; }
        const saved = isNew ? await api.pharmacy.createBill(buildDto()) : await api.pharmacy.updateBill(id, buildDto());
        billId = saved.id;
      }
      const res = await api.pharmacy.finalizeBill(billId, { payments: pmts, credit_note_ids });
      setBill(res);
      if (isNew) navigate(`/pharmacy/billing/${res.id}`, { replace: true });
    } catch (e) { setError(e?.response?.data?.message || e?.message || 'Failed to finalize bill'); }
    finally { setSaving(false); }
  };

  const handleCancel = async (dto) => {
    setSaving(true); setError('');
    setCancelDialog(false);
    try {
      const res = await api.pharmacy.cancelBill(id, dto);
      setBill(res);
    } catch (e) { setError(e?.response?.data?.message || e?.message || 'Failed to cancel bill'); }
    finally { setSaving(false); }
  };

  const handleReturn = async (dto) => {
    setSaving(true); setError('');
    setReturnDialog(false);
    try {
      const ret = await api.pharmacy.returnBillItems(id, dto);
      setLastReturn(ret);
      const res = await api.pharmacy.getBill(id);
      setBill(res);
      setCartItems((res.items || []).map(it => calcLineTotals({ ...it, _key: `existing_${it.id}`, _base_unit: '', _sub_unit: null, _sub_per_base: 1, _is_sub_sellable: false, _max_stock: 999 })));
      printReturnNote(ret, selectedPharmacy);
    } catch (e) { setError(e?.response?.data?.message || e?.message || 'Failed to process return'); }
    finally { setSaving(false); }
  };

  // ── Global keyboard shortcuts (Tally-style) ──
  useEffect(() => {
    if (isReadOnly) return undefined;
    const onKey = (e) => {
      if (payDialog || cancelDialog || returnDialog || selectedGroup || addingDoctor) return;
      const ctrl = e.ctrlKey || e.metaKey;
      if (ctrl && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        if (!saving) handleSaveDraft();
      } else if (ctrl && e.key === 'Enter') {
        e.preventDefault();
        if (!saving && cartItems.length && patient.patient_name) setPayDialog(true);
      } else if (e.altKey && (e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
        searchBoxRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const status = bill?.status;
  const STATUS_COLOR = { DRAFT: '#888', FINALIZED: '#2e7d32', CANCELLED: '#c62828', PARTIALLY_RETURNED: '#e65100' };

  if (loading) return (
    <DashboardLayout><DashboardNavbar />
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress sx={{ color: TEAL }} /></Box>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* ── Header bar ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <IconButton size="small" onClick={() => navigate('/pharmacy/billing')} sx={{ color: TEAL }}><ArrowBackIcon /></IconButton>
            <MDTypography variant="h6" sx={{ fontWeight: 700, color: TEAL }}>
              {isNew ? 'New Bill' : `Bill: ${bill?.bill_no || `#${id}`}`}
            </MDTypography>
            {status && <Chip label={status.replace('_', ' ')} size="small" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#fff', background: STATUS_COLOR[status] || '#888' }} />}
            {/* Store location selector */}
            {locations.length > 1 && !isReadOnly && (
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel sx={{ fontSize: '0.82rem' }}>Store Location *</InputLabel>
                <Select value={locationId || ''} label="Store Location *"
                  onChange={e => setLocationId(e.target.value)}
                  sx={{
                    borderRadius: 2, fontSize: '0.82rem',
                    '& .MuiSelect-select': {
                      py: '9.5px',
                      fontWeight: locationId ? 700 : 400,
                      color: locationId ? TEAL : 'inherit',
                    },
                  }}>
                  {locations.map(l => <MenuItem key={l.id} value={l.id} sx={{ fontSize: '0.82rem', py: 1 }}>{l.name}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            {locations.length === 1 && (
              <Chip label={locations[0].name} size="small" variant="outlined"
                sx={{ fontSize: '0.7rem', color: TEAL, borderColor: `${TEAL}66` }} />
            )}
            {locations.length === 0 && (
              <Chip label="No location loaded" size="small" sx={{ fontSize: '0.7rem', color: '#e74c3c', background: '#fdecea' }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {bill && <Button size="small" startIcon={<PrintIcon />} onClick={() => printBill(bill)} variant="outlined"
              sx={{ color: TEAL, borderColor: TEAL, fontSize: '0.75rem', borderRadius: 2, '&:hover': { color: TEAL, borderColor: TEAL, background: `${TEAL}0d` } }}>Print</Button>}
            {bill && (
              <Button size="small" startIcon={<TimelineIcon />} onClick={openTimeline} variant="outlined"
                sx={{ color: '#1a237e', borderColor: '#3949ab', fontSize: '0.75rem', borderRadius: 2, '&:hover': { color: '#1a237e', borderColor: '#3949ab', background: '#e8eaf6' } }}>
                Timeline
              </Button>
            )}
            {(status === 'FINALIZED' || status === 'PARTIALLY_RETURNED' || status === 'CANCELLED') && (
              <Button size="small" startIcon={<PrintIcon />} onClick={() => printDuplicateBill(bill)} variant="outlined"
                sx={{ color: '#6d4c41', borderColor: '#a1887f', fontSize: '0.75rem', borderRadius: 2, '&:hover': { color: '#6d4c41', borderColor: '#a1887f', background: 'rgba(109,76,65,0.06)' } }}>
                Print Duplicate
              </Button>
            )}
            {lastReturn && (
              <Button size="small" startIcon={<PrintIcon />} onClick={() => printReturnNote(lastReturn, selectedPharmacy)} variant="outlined"
                sx={{ color: '#e65100', borderColor: '#e65100', fontSize: '0.75rem', borderRadius: 2, '&:hover': { color: '#e65100', borderColor: '#e65100', background: 'rgba(230,81,0,0.06)' } }}>
                Print Return Note
              </Button>
            )}
            {(status === 'FINALIZED' || status === 'PARTIALLY_RETURNED' || status === 'CANCELLED') && (
              <Button size="small" onClick={() => navigate('/pharmacy/billing/returns')} variant="outlined"
                sx={{ color: '#e65100', borderColor: '#e65100', fontSize: '0.75rem', borderRadius: 2, '&:hover': { color: '#e65100', borderColor: '#e65100', background: 'rgba(230,81,0,0.06)' } }}>
                Returns List
              </Button>
            )}
            {!isReadOnly && (
              <>
                <Button size="small" onClick={handleSaveDraft} disabled={saving} variant="outlined"
                  sx={{ color: TEAL, borderColor: TEAL, fontSize: '0.75rem', borderRadius: 2, '&:hover': { color: TEAL, borderColor: TEAL, background: `${TEAL}0d` } }}>
                  {saving ? <CircularProgress size={14} /> : 'Save Draft'}
                </Button>
                <Button size="small" onClick={() => setPayDialog(true)} disabled={saving || !cartItems.length || !patient.patient_name} variant="contained"
                  startIcon={<PaymentIcon sx={{ fontSize: '16px !important', color: '#fff !important' }} />}
                  sx={{
                    background: 'linear-gradient(135deg, #00798E 0%, #005462 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #009ab5 0%, #006b7e 100%)',
                      boxShadow: '0 6px 22px rgba(0,121,142,0.5)',
                      transform: 'translateY(-1px)',
                    },
                    '&:active': { transform: 'translateY(0)', boxShadow: '0 2px 8px rgba(0,121,142,0.3)' },
                    '&.Mui-disabled': { background: '#b0bec5 !important', boxShadow: 'none' },
                    color: '#fff !important',
                    fontSize: '0.82rem',
                    fontWeight: 800,
                    borderRadius: '20px',
                    px: 2.5,
                    py: 0.8,
                    letterSpacing: 0.6,
                    boxShadow: '0 4px 14px rgba(0,121,142,0.38)',
                    transition: 'all 0.2s ease',
                    textTransform: 'none',
                    minWidth: 118,
                  }}>
                  Finalize
                </Button>
              </>
            )}
            {status === 'FINALIZED' && (
              <>
                <Button size="small" onClick={() => setCancelDialog(true)} variant="contained"
                  sx={{ background: '#c62828', '&:hover': { background: '#8e0000' }, color: '#fff', fontSize: '0.78rem', fontWeight: 700, borderRadius: 2, px: 2.5, letterSpacing: 0.3 }}>
                  Cancel Bill
                </Button>
                <Button size="small" onClick={() => setReturnDialog(true)} variant="outlined" sx={{ color: TEAL, borderColor: TEAL, fontSize: '0.75rem', borderRadius: 2, '&:hover': { color: TEAL, borderColor: TEAL, background: `${TEAL}0d` } }}>Return Items</Button>
              </>
            )}
            {status === 'PARTIALLY_RETURNED' && (
              <>
                <Button size="small" onClick={() => setCancelDialog(true)} variant="contained"
                  sx={{ background: '#c62828', '&:hover': { background: '#8e0000' }, color: '#fff', fontSize: '0.78rem', fontWeight: 700, borderRadius: 2, px: 2.5, letterSpacing: 0.3 }}>
                  Cancel Bill
                </Button>
                <Button size="small" onClick={() => setReturnDialog(true)} variant="outlined" sx={{ color: TEAL, borderColor: TEAL, fontSize: '0.75rem', borderRadius: 2, '&:hover': { color: TEAL, borderColor: TEAL, background: `${TEAL}0d` } }}>Return More</Button>
              </>
            )}
          </Box>
        </Box>

        {!isReadOnly && (
          <KeyboardHintBar storageKey="pharm-billing">
            <b>Alt+A</b> focus item search · <b>↓ ↵</b> pick item · in Qty box <b>↵</b> adds to cart · <b>Ctrl+S</b> Save Draft · <b>Ctrl+↵</b> Finalize
          </KeyboardHintBar>
        )}

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, fontSize: '0.8rem' }}>{error}</Alert>}

        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', lg: 'row' } }}>
          {/* ── Left column ── */}
          <Box sx={{ flex: '0 0 340px', display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* Patient */}
            <Card sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
              <SectionHeader icon={<PersonIcon sx={{ color: TEAL, fontSize: 18 }} />} title="Patient" />

              {/* Patient search — live autocomplete (search mode only) */}
              {patientMode === 'search' && !isReadOnly && (
                <Box sx={{ mb: 1.5 }}>
                  <Autocomplete
                    size="small"
                    freeSolo
                    options={patientResults}
                    inputValue={patientSearch}
                    onInputChange={handlePatientInputChange}
                    onChange={(_, val) => { if (val && typeof val === 'object') selectPatient(val); }}
                    loading={patientSearching}
                    getOptionLabel={(opt) => {
                      if (typeof opt === 'string') return opt;
                      return opt.full_name?.trim() || [opt.first_name, opt.last_name].filter(Boolean).join(' ') || '';
                    }}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    filterOptions={(x) => x}
                    noOptionsText={patientSearch.length >= 2 ? 'No patients found' : 'Type to search…'}
                    sx={{ '& .MuiOutlinedInput-root': { minHeight: '40px !important' } }}
                    renderOption={(props, opt) => {
                      const name = opt.full_name?.trim() || [opt.first_name, opt.middle_name, opt.last_name].filter(Boolean).join(' ') || '—';
                      const mobile = opt.mobile_number || opt.phones?.[0]?.phone_number || '';
                      const code = opt.external_code || '';
                      return (
                        <Box component="li" {...props} key={`${opt.is_guest ? 'g' : 'c'}_${opt.id}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: '8px !important' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{name}</Typography>
                            {opt.is_guest && (
                              <Box sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#fff', background: '#ffa726', borderRadius: '4px', px: 0.6, py: '1px', lineHeight: 1.4 }}>GUEST</Box>
                            )}
                          </Box>
                          <Typography sx={{ fontSize: '0.7rem', color: '#888' }}>
                            {[mobile, code].filter(Boolean).join(' · ')}
                          </Typography>
                        </Box>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search by name, mobile or patient code…"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.82rem', minHeight: '40px !important' } }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              {patientSearching ? <CircularProgress size={14} sx={{ color: TEAL }} /> : <SearchIcon fontSize="small" sx={{ color: '#aaa' }} />}
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => setPatientMode('manual')}
                      variant="outlined"
                      startIcon={<PersonAddAlt1Icon sx={{ fontSize: '14px !important' }} />}
                      sx={{
                        color: '#718096', borderColor: '#cbd5e0', fontSize: '0.72rem',
                        borderRadius: 2, py: 0.6, px: 1.5, fontWeight: 500, textTransform: 'none',
                        '&:hover': { borderColor: TEAL, color: TEAL, background: `${TEAL}08` },
                      }}>
                      Others
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Selected patient chip (found mode) */}
              {patientMode === 'found' && (
                <Box sx={{ mb: 1.5, p: 1.2, borderRadius: 2, background: '#e8f5e9', border: '1px solid #a5d6a7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#2e7d32' }}>{patient.patient_name}</Typography>
                      {patient.irm_pharm_guest_patients_id && (
                        <Box sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#fff', background: '#ffa726', borderRadius: '4px', px: 0.6, py: '1px', lineHeight: 1.4 }}>GUEST</Box>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: '0.72rem', color: '#555' }}>
                      {[patient.patient_mobile, patient.patient_age ? `Age: ${patient.patient_age}` : ''].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                  {!isReadOnly && (
                    <IconButton size="small" onClick={() => { setPatientMode('search'); setPatient(EMPTY_PATIENT); setPatientSearch(''); setCreditNotes([]); }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}

              {/* Patient fields — shown in manual mode or after selection (to allow editing) */}
              {(patientMode === 'manual' || patientMode === 'found') && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.2 }}>
                  <TextField size="small" label="Patient Name *" value={patient.patient_name} disabled={isReadOnly}
                    onChange={e => setPatient(p => ({ ...p, patient_name: e.target.value }))}
                    sx={{ gridColumn: '1/-1', '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.8rem' } }} />
                  <TextField size="small" label="Mobile" value={patient.patient_mobile} disabled={isReadOnly}
                    onChange={e => setPatient(p => ({ ...p, patient_mobile: e.target.value }))}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.8rem' } }} />
                  <TextField size="small" select label="Gender" value={patient.patient_gender} disabled={isReadOnly}
                    onChange={e => setPatient(p => ({ ...p, patient_gender: e.target.value }))}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.82rem' }, '& .MuiSelect-select': { fontWeight: patient.patient_gender ? 600 : 400 } }}>
                    <MenuItem value=""><em style={{ color: '#aaa' }}>—</em></MenuItem>
                    {[['M', 'Male'], ['F', 'Female'], ['O', 'Other']].map(([v, l]) => <MenuItem key={v} value={v} sx={{ fontSize: '0.82rem', py: 1 }}>{l}</MenuItem>)}
                  </TextField>
                  <TextField size="small" label="Age" type="number" value={patient.patient_age} disabled={isReadOnly}
                    onChange={e => setPatient(p => ({ ...p, patient_age: e.target.value }))}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.8rem' } }} />
                  <TextField size="small" label="Address" value={patient.patient_address} disabled={isReadOnly} multiline rows={2}
                    onChange={e => setPatient(p => ({ ...p, patient_address: e.target.value }))}
                    sx={{ gridColumn: '1/-1', '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.8rem' } }} />
                  {patientMode === 'manual' && !isReadOnly && (
                    <Button size="small" onClick={() => { setPatientMode('search'); setPatient(EMPTY_PATIENT); setPatientSearch(''); }}
                      sx={{ gridColumn: '1/-1', color: '#888', fontSize: '0.7rem', textDecoration: 'underline', justifyContent: 'flex-start', p: 0, minWidth: 0, '&:hover': { color: '#555', background: 'transparent', textDecoration: 'underline' } }}>
                      ← Back to patient search
                    </Button>
                  )}
                </Box>
              )}
            </Card>

            {/* Doctor */}
            <Card sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
              <SectionHeader icon={<LocalPharmacyIcon sx={{ color: TEAL, fontSize: 18 }} />} title="Doctor" />

              <FormControlLabel
                control={<Checkbox size="small" checked={isSelf} onChange={() => { setIsSelf(v => !v); if (!isSelf) { setSelectedDoctor(null); setAddingDoctor(false); setTimeout(() => searchBoxRef.current?.focus(), 60); } }} disabled={isReadOnly} sx={{ color: TEAL, '&.Mui-checked': { color: TEAL }, p: 0.5 }} />}
                label={<Typography sx={{ fontSize: '0.8rem' }}>Self (no prescription)</Typography>}
                sx={{ ml: 0 }}
              />

              {!isSelf && !addingDoctor && (
                <Autocomplete
                  size="small"
                  fullWidth
                  options={doctorOptions}
                  getOptionLabel={o => (typeof o === 'string' ? o : o.name || '')}
                  value={selectedDoctor}
                  disabled={isReadOnly}
                  onChange={(_, v) => {
                    if (v?.__addNew) { setAddingDoctor(true); setNewDoctorName(''); setSelectedDoctor(null); }
                    else { setSelectedDoctor(v); if (v) setTimeout(() => searchBoxRef.current?.focus(), 60); }
                  }}
                  onInputChange={(_, v) => {
                    api.pharmacy.listDoctors(v).then(r => setDoctorOptions(r || [])).catch(() => {});
                  }}
                  filterOptions={(opts, state) => {
                    const filtered = opts.filter(o => o.name?.toLowerCase().includes(state.inputValue.toLowerCase()));
                    filtered.push({ __addNew: true, name: `Add new doctor${state.inputValue ? `: "${state.inputValue}"` : ''}`, _inputVal: state.inputValue });
                    return filtered;
                  }}
                  renderOption={(props, opt) => (
                    <Box component="li" {...props} key={opt.__addNew ? '__add' : opt.id}
                      sx={opt.__addNew ? { color: TEAL, fontWeight: 700, fontSize: '0.8rem', borderTop: '1px solid #f0f0f0' } : { fontSize: '0.8rem' }}>
                      {opt.__addNew ? `+ ${opt.name}` : opt.name}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} inputRef={doctorInputRef} label="Doctor name" sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.8rem' } }} />
                  )}
                  noOptionsText={
                    <Box sx={{ py: 0.5 }}>
                      <Typography sx={{ fontSize: '0.78rem', color: '#888', mb: 0.5 }}>No doctor found</Typography>
                      <Button size="small" onClick={() => { setAddingDoctor(true); setNewDoctorName(''); }} sx={{ color: TEAL, fontSize: '0.75rem', fontWeight: 700, p: 0, '&:hover': { color: TEAL, background: 'transparent' } }}>
                        + Add new doctor
                      </Button>
                    </Box>
                  }
                />
              )}

              {!isSelf && addingDoctor && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    size="small" autoFocus
                    label="New doctor name *"
                    value={newDoctorName}
                    onChange={e => setNewDoctorName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Escape') { setAddingDoctor(false); setNewDoctorName(''); } }}
                    sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.8rem' } }}
                  />
                  <Button size="small" variant="contained" disabled={!newDoctorName.trim() || savingDoctor}
                    onClick={async () => {
                      if (!newDoctorName.trim()) return;
                      setSavingDoctor(true);
                      try {
                        const doc = await api.pharmacy.createDoctor({ name: newDoctorName.trim() });
                        setDoctorOptions(prev => [...prev, doc]);
                        setSelectedDoctor(doc);
                        setAddingDoctor(false);
                        setNewDoctorName('');
                      } catch { /* ignore */ }
                      finally { setSavingDoctor(false); }
                    }}
                    sx={{ background: TEAL, '&:hover': { background: '#005f70' }, borderRadius: 2, fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap', height: 36 }}>
                    {savingDoctor ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Save'}
                  </Button>
                  <IconButton size="small" onClick={() => { setAddingDoctor(false); setNewDoctorName(''); }} sx={{ height: 36, color: '#888' }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {!isSelf && selectedDoctor && !addingDoctor && (
                <Box sx={{ mt: 1, p: 1, borderRadius: 2, background: '#f0f8fa', border: `1px solid ${TEAL}33`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: TEAL }}>{selectedDoctor.name}</Typography>
                  {!isReadOnly && <IconButton size="small" onClick={() => setSelectedDoctor(null)} sx={{ color: '#aaa' }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>}
                </Box>
              )}
            </Card>

            {/* GST Type */}
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#2d3748' }}>
                    {isInterstate ? 'Interstate (IGST)' : 'Intrastate (CGST + SGST)'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#718096', mt: 0.25 }}>
                    {isInterstate ? 'Cross-state — full tax as IGST' : 'Default — tax split into CGST & SGST'}
                  </Typography>
                </Box>
                <Switch
                  checked={isInterstate}
                  disabled={isReadOnly}
                  onChange={e => setIsInterstate(e.target.checked)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#e65100' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#e65100' },
                  }}
                />
              </Box>
              {isInterstate && (
                <Box sx={{ mt: 1, px: 1.2, py: 0.6, borderRadius: 1.5, background: '#fff3e0', border: '1px solid #ffcc80' }}>
                  <Typography sx={{ fontSize: '0.7rem', color: '#e65100', fontWeight: 600 }}>
                    IGST mode — applies to all items in this bill
                  </Typography>
                </Box>
              )}
            </Card>
          </Box>

          {/* ── Right column — Items ── */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <Card sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
              <SectionHeader icon={<ShoppingCartIcon sx={{ color: TEAL, fontSize: 18 }} />} title="Items" />

              {/* Search box */}
              {!isReadOnly && (
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ position: 'relative' }}>
                    <TextField inputRef={searchBoxRef} size="small" fullWidth
                      placeholder="Search by item name or scan barcode…"
                      value={itemSearch}
                      onChange={e => handleItemSearch(e.target.value)}
                      onKeyDown={handleItemSearchKeyDown}
                      InputProps={{ startAdornment: <InputAdornment position="start">{itemSearching ? <CircularProgress size={14} sx={{ color: TEAL }} /> : <SearchIcon fontSize="small" sx={{ color: '#aaa' }} />}</InputAdornment> }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.8rem' } }} />
                    {itemSearch.length >= 2 && itemSearch.length < 6 && !itemSearching && itemGroups.length === 0 && (
                      <Typography sx={{ fontSize: '0.68rem', color: '#aaa', mt: 0.5, pl: 0.5 }}>
                        Press <b>Enter</b> to search
                      </Typography>
                    )}

                    {/* Item dropdown */}
                    {itemGroups.length > 0 && (
                      <Paper sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, maxHeight: 280, overflow: 'auto', border: `1px solid ${TEAL}33`, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', mt: 0.5 }}>
                        {itemGroups.map((g, i) => {
                          const totalBase = g.batches.reduce((s, b) => s + Number(b.qty_base_units), 0);
                          const subPerBase = Number(g.item.sub_units_per_base || 1);
                          const isSubSell = g.item.is_sub_unit_sellable === 'Y' && g.item.sub_unit;
                          const stockLabel = isSubSell
                            ? `${totalBase.toFixed(0)} ${g.item.base_unit?.name || 'units'} / ${(totalBase * subPerBase).toFixed(0)} ${g.item.sub_unit?.name || 'sub'}`
                            : `${totalBase.toFixed(0)} ${g.item.base_unit?.name || 'units'}`;
                          return (
                            <Box key={i} onClick={() => selectItemGroup(g)}
                              ref={el => { itemResultRefs.current[i] = el; }}
                              tabIndex={0}
                              onKeyDown={itemResultKeyDown(i)}
                              sx={{ p: 1.5, cursor: 'pointer', borderBottom: i < itemGroups.length - 1 ? '1px solid #f5f5f5' : 'none', outline: 'none', '&:hover, &:focus': { background: '#f0f8fa' }, '&:focus': { boxShadow: `inset 3px 0 0 ${TEAL}` }, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                  {g.item.name}{g.variant ? ` — ${g.variant.variant_name}` : ''}
                                </Typography>
                                <Typography sx={{ fontSize: '0.7rem', color: '#888' }}>
                                  {g.batches.length} batch{g.batches.length > 1 ? 'es' : ''} · Stock: {stockLabel}
                                </Typography>
                              </Box>
                              <Typography sx={{ fontSize: '0.8rem', fontFamily: 'monospace', fontWeight: 700, color: TEAL }}>
                                ₹{Number(g.batches[0]?.mrp || 0).toFixed(2)}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Paper>
                    )}
                  </Box>

                  {/* QtyPanel */}
                  {selectedGroup && (
                    <Box sx={{ mt: 1 }}>
                      <QtyPanel group={selectedGroup} maxDiscount={maxDiscount} onAdd={handleAddCartRows} onClose={() => setSelectedGroup(null)} isInterstate={isInterstate} />
                    </Box>
                  )}
                </Box>
              )}

              {/* Cart table — Excel style */}
              <Box sx={{ overflowX: 'auto', border: '1px solid #c8d0d8', borderRadius: 1 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: '0.78rem' }}>
                  <colgroup>
                    <col style={{ width: 34 }} />
                    <col style={{ width: '22%', minWidth: 160 }} />
                    <col style={{ width: 88 }} />
                    <col style={{ width: 82 }} />
                    <col style={{ width: 58 }} />
                    <col style={{ width: 72 }} />
                    <col style={{ width: 78 }} />
                    <col style={{ width: 72 }} />
                    <col style={{ width: 92 }} />
                    {!isReadOnly && <col style={{ width: 32 }} />}
                  </colgroup>
                  <thead>
                    <tr style={{ background: '#eef2f7', position: 'sticky', top: 0, zIndex: 1 }}>
                      {[
                        { label: '#',          align: 'center' },
                        { label: 'Item',        align: 'left'   },
                        { label: 'Batch',       align: 'left'   },
                        { label: 'Expiry',      align: 'center' },
                        { label: 'Unit',        align: 'center' },
                        { label: 'Qty',         align: 'center' },
                        { label: 'MRP (₹)',     align: 'right'  },
                        { label: `Disc %`,      align: 'center' },
                        { label: 'Amount (₹)',  align: 'right'  },
                        ...(isReadOnly ? [] : [{ label: '', align: 'center' }]),
                      ].map((h, idx) => (
                        <th key={idx} style={{
                          padding: '6px 7px', textAlign: h.align, fontWeight: 700,
                          fontSize: '0.67rem', color: '#4a5568', textTransform: 'uppercase',
                          letterSpacing: '0.4px', borderRight: '1px solid #c8d0d8',
                          borderBottom: '2px solid #aab4c0', whiteSpace: 'nowrap',
                          background: 'inherit',
                        }}>{h.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cartItems.length === 0 ? (
                      <tr>
                        <td colSpan={isReadOnly ? 9 : 10} style={{ padding: '28px 0', textAlign: 'center', color: '#aaa', fontSize: '0.82rem', background: '#fafbfc' }}>
                          Search above and add items to the bill
                        </td>
                      </tr>
                    ) : cartItems.map((it, i) => {
                      const returnedBase = Number(it.returned_qty || 0);
                      const isFullyRet = isReadOnly && (it.is_returned === 'Y' || (returnedBase > 0 && returnedBase >= Number(it.qty_base_deducted)));
                      const isPartialRet = isReadOnly && !isFullyRet && returnedBase > 0;
                      const effItem = isReadOnly ? effectiveById[String(it.id)] : null;
                      const isEven = i % 2 === 0;
                      const rowBg = isFullyRet ? '#fff5f5' : isPartialRet ? '#fff8f0' : (isEven ? '#ffffff' : '#f7f9fc');
                      const cellStyle = {
                        padding: '0', borderRight: '1px solid #dde3ea',
                        borderBottom: '1px solid #dde3ea', verticalAlign: 'middle',
                        background: rowBg,
                      };
                      const textCell = (content, extra = {}) => (
                        <td style={{ ...cellStyle, padding: '5px 7px', ...extra }}>{content}</td>
                      );
                      return (
                        <tr key={it._key || i}
                          style={{ cursor: 'default' }}
                          onMouseEnter={e => { Array.from(e.currentTarget.cells).forEach(c => { c.style.background = '#fffbea'; }); }}
                          onMouseLeave={e => { Array.from(e.currentTarget.cells).forEach(c => { c.style.background = rowBg; }); }}>

                          {/* # */}
                          {textCell(
                            <span style={{ color: '#9aa5b4', fontSize: '0.72rem', display: 'block', textAlign: 'center' }}>{i + 1}</span>
                          )}

                          {/* Item */}
                          {textCell(
                            <>
                              <div style={{ fontWeight: 600, color: '#1a202c', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.item_name}</div>
                              {it.variant_name && <div style={{ fontSize: '0.68rem', color: '#718096' }}>{it.variant_name}</div>}
                              {it.hsn_code && <div style={{ fontSize: '0.63rem', color: '#a0aec0', fontFamily: 'monospace' }}>HSN {it.hsn_code}</div>}
                            </>
                          )}

                          {/* Batch */}
                          {textCell(
                            <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: '#2d3748' }}>{it.batch_no || '—'}</span>
                          )}

                          {/* Expiry */}
                          {textCell(
                            <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: isExpiredDate(it.expiry_date) ? '#e53e3e' : '#4a5568', textAlign: 'center', display: 'block' }}>
                              {fmtExpiry(it.expiry_date)}
                            </span>
                          )}

                          {/* Unit */}
                          {textCell(
                            <span style={{ fontSize: '0.72rem', color: '#4a5568', display: 'block', textAlign: 'center' }}>
                              {it.unit_type === 'SUB' ? (it._sub_unit || 'Sub') : (it._base_unit || 'Base')}
                            </span>
                          )}

                          {/* Qty — editable cell */}
                          <td style={{ ...cellStyle, padding: 0 }}>
                            {isReadOnly ? (
                              (() => {
                                const displayQty = it.unit_type === 'SUB' ? Number(it.qty_sub_units) : Number(it.qty_units);
                                if (returnedBase > 0 || isFullyRet) {
                                  const remainingBase = Math.max(0, Number(it.qty_base_deducted) - returnedBase);
                                  const subPerBase = Number(it._sub_per_base || 1);
                                  const displayRemaining = it.unit_type === 'SUB' ? (remainingBase * subPerBase) : remainingBase;
                                  const displayReturned = it.unit_type === 'SUB' ? (returnedBase * subPerBase) : returnedBase;
                                  return (
                                    <span style={{ display: 'block', textAlign: 'center', padding: '5px 4px' }}>
                                      <span style={{ fontFamily: 'monospace', color: '#bbb', textDecoration: 'line-through', fontSize: '0.72rem', display: 'block' }}>
                                        {displayQty}
                                      </span>
                                      <span style={{ fontSize: '0.6rem', color: '#e65100', display: 'block' }}>−{displayReturned}</span>
                                      {isFullyRet ? (
                                        <span style={{ fontSize: '0.6rem', background: '#fee2e2', color: '#c62828', fontWeight: 700, borderRadius: '3px', padding: '1px 4px', display: 'inline-block' }}>
                                          RETURNED
                                        </span>
                                      ) : (
                                        <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#2e7d32', fontSize: '0.78rem', display: 'block' }}>
                                          {displayRemaining}
                                        </span>
                                      )}
                                    </span>
                                  );
                                }
                                return (
                                  <span style={{ display: 'block', textAlign: 'center', padding: '5px 4px', fontFamily: 'monospace', fontWeight: 600 }}>
                                    {displayQty}
                                  </span>
                                );
                              })()
                            ) : (
                              <input type="number" min="0" step="1"
                                value={it.unit_type === 'SUB' ? it.qty_sub_units : it.qty_units}
                                onChange={e => updateCartQty(it._key, it.unit_type === 'SUB' ? 'qty_sub_units' : 'qty_units', e.target.value)}
                                style={{
                                  width: '100%', height: '100%', minHeight: 32, border: 'none', outline: 'none',
                                  background: 'transparent', textAlign: 'center', fontFamily: 'monospace',
                                  fontSize: '0.8rem', fontWeight: 600, padding: '4px 4px', boxSizing: 'border-box',
                                  color: '#1a202c',
                                }}
                                onFocus={e => { e.target.style.background = '#ebf8ff'; e.target.style.boxShadow = 'inset 0 0 0 2px #4299e1'; }}
                                onBlur={e => { e.target.style.background = 'transparent'; e.target.style.boxShadow = 'none'; }}
                              />
                            )}
                          </td>

                          {/* MRP — editable in draft */}
                          <td style={{ ...cellStyle, padding: 0 }}>
                            {isReadOnly ? (
                              <span style={{ display: 'block', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.78rem', color: '#2d3748', padding: '5px 7px' }}>
                                {Number(it.mrp).toFixed(2)}
                              </span>
                            ) : (
                              <input type="number" min="0" step="0.01"
                                value={it.mrp}
                                onChange={e => updateCartMrp(it._key, e.target.value)}
                                style={{
                                  width: '100%', height: '100%', minHeight: 32, border: 'none', outline: 'none',
                                  background: 'transparent', textAlign: 'right', fontFamily: 'monospace',
                                  fontSize: '0.78rem', padding: '4px 7px', boxSizing: 'border-box', color: '#1a202c',
                                }}
                                onFocus={e => { e.target.style.background = '#ebf8ff'; e.target.style.boxShadow = 'inset 0 0 0 2px #4299e1'; }}
                                onBlur={e => { e.target.style.background = 'transparent'; e.target.style.boxShadow = 'none'; }}
                              />
                            )}
                          </td>

                          {/* Disc % — editable cell */}
                          <td style={{ ...cellStyle, padding: 0 }}>
                            {isReadOnly ? (
                              <span style={{ display: 'block', textAlign: 'center', padding: '5px 4px', fontFamily: 'monospace' }}>
                                {Number(it.discount_percentage).toFixed(1)}%
                              </span>
                            ) : (
                              <Tooltip title={`Max ${maxDiscount}%`} placement="top" arrow>
                                <input type="number" min="0" max={maxDiscount} step="0.5"
                                  value={it.discount_percentage}
                                  onChange={e => updateCartDiscount(it._key, e.target.value)}
                                  style={{
                                    width: '100%', height: '100%', minHeight: 32, border: 'none', outline: 'none',
                                    background: 'transparent', textAlign: 'center', fontFamily: 'monospace',
                                    fontSize: '0.78rem', padding: '4px 4px', boxSizing: 'border-box', color: '#1a202c',
                                  }}
                                  onFocus={e => { e.target.style.background = '#ebf8ff'; e.target.style.boxShadow = 'inset 0 0 0 2px #4299e1'; }}
                                  onBlur={e => { e.target.style.background = 'transparent'; e.target.style.boxShadow = 'none'; }}
                                />
                              </Tooltip>
                            )}
                          </td>

                          {/* Amount */}
                          {textCell(
                            (() => {
                              if (isReadOnly && returnedBase > 0) {
                                const effAmt = effItem ? Number(effItem.line_total) : 0;
                                return (
                                  <span style={{ display: 'block', textAlign: 'right', padding: '5px 4px' }}>
                                    <span style={{ fontFamily: 'monospace', color: '#bbb', textDecoration: 'line-through', fontSize: '0.72rem', display: 'block' }}>
                                      ₹{Number(it.line_total).toFixed(2)}
                                    </span>
                                    <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem', color: isFullyRet ? '#c62828' : TEAL }}>
                                      ₹{effAmt.toFixed(2)}
                                    </span>
                                  </span>
                                );
                              }
                              return (
                                <span style={{ display: 'block', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem', color: TEAL }}>
                                  {Number(it.line_total).toFixed(2)}
                                </span>
                              );
                            })()
                          )}

                          {/* Delete */}
                          {!isReadOnly && (
                            <td style={{ ...cellStyle, padding: '2px', textAlign: 'center' }}>
                              <button onClick={() => removeCartItem(it._key)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fc8181', padding: '2px 4px', borderRadius: 3, lineHeight: 1 }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#fff5f5'; e.currentTarget.style.color = '#e53e3e'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#fc8181'; }}
                                title="Remove">
                                ✕
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}

                    {/* Summary footer row */}
                    {cartItems.length > 0 && (
                      <tr style={{ background: '#f0f4f8', borderTop: '2px solid #aab4c0' }}>
                        <td colSpan={isReadOnly ? 7 : 7} style={{ padding: '6px 8px', fontSize: '0.72rem', color: '#718096', fontStyle: 'italic', borderRight: '1px solid #c8d0d8' }}>
                          {cartItems.length} item{cartItems.length !== 1 ? 's' : ''}
                          {totals.total_discount > 0 && <span style={{ marginLeft: 10, color: '#e53e3e' }}>Discount: −{Number(totals.total_discount).toFixed(2)}</span>}
                          {totals.total_cgst > 0 && <span style={{ marginLeft: 10, color: '#718096' }}>CGST: {Number(totals.total_cgst).toFixed(2)}</span>}
                          {totals.total_sgst > 0 && <span style={{ marginLeft: 10, color: '#718096' }}>SGST: {Number(totals.total_sgst).toFixed(2)}</span>}
                          {totals.total_igst > 0 && <span style={{ marginLeft: 10, color: '#718096' }}>IGST: {Number(totals.total_igst).toFixed(2)}</span>}
                          {Math.abs(Number(bill?.round_off_amount || 0)) >= 0.005 && (
                            <span style={{ marginLeft: 10, color: '#8d6e63' }}>Round Off: {Number(bill.round_off_amount) > 0 ? '+' : '−'}{Math.abs(Number(bill.round_off_amount)).toFixed(2)}</span>
                          )}
                        </td>
                        <td colSpan={isReadOnly ? 2 : 3} style={{ padding: '6px 8px', textAlign: 'right' }}>
                          <span style={{ fontSize: '0.72rem', color: '#718096', marginRight: 8 }}>NET TOTAL</span>
                          <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.95rem', color: TEAL }}>₹{Number(totals.net_amount).toFixed(2)}</span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Box>
            </Card>

            {/* Totals summary card */}
            {cartItems.length > 0 && (
              <Card sx={{ p: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: `1px solid ${TEAL}22` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  {/* Tax breakdown chips */}
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1.5, background: '#f6f8fa', border: '1px solid #e2e8f0' }}>
                      <span style={{ fontSize: '0.68rem', color: '#718096' }}>Sub Total </span>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600, color: '#2d3748' }}>₹{Number(totals.sub_total).toFixed(2)}</span>
                    </Box>
                    {totals.total_discount > 0 && (
                      <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1.5, background: '#fff5f5', border: '1px solid #fed7d7' }}>
                        <span style={{ fontSize: '0.68rem', color: '#e53e3e' }}>Discount </span>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600, color: '#e53e3e' }}>−₹{Number(totals.total_discount).toFixed(2)}</span>
                      </Box>
                    )}
                    {totals.total_cgst > 0 && (
                      <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1.5, background: '#f0f4f8', border: '1px solid #e2e8f0' }}>
                        <span style={{ fontSize: '0.68rem', color: '#718096' }}>CGST </span>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#4a5568' }}>₹{Number(totals.total_cgst).toFixed(2)}</span>
                      </Box>
                    )}
                    {totals.total_sgst > 0 && (
                      <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1.5, background: '#f0f4f8', border: '1px solid #e2e8f0' }}>
                        <span style={{ fontSize: '0.68rem', color: '#718096' }}>SGST </span>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#4a5568' }}>₹{Number(totals.total_sgst).toFixed(2)}</span>
                      </Box>
                    )}
                    {totals.total_igst > 0 && (
                      <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1.5, background: '#f0f4f8', border: '1px solid #e2e8f0' }}>
                        <span style={{ fontSize: '0.68rem', color: '#718096' }}>IGST </span>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#4a5568' }}>₹{Number(totals.total_igst).toFixed(2)}</span>
                      </Box>
                    )}
                    {(() => {
                      const ro = isReadOnly ? Number(bill?.round_off_amount || 0) : Number(totals.round_off_amount || 0);
                      if (Math.abs(ro) < 0.005) return null;
                      return (
                        <>
                          <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1.5, background: '#f6f8fa', border: '1px solid #e2e8f0' }}>
                            <span style={{ fontSize: '0.68rem', color: '#718096' }}>Sub Total </span>
                            <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#4a5568' }}>₹{Number(totals.raw_net_amount ?? totals.net_amount).toFixed(2)}</span>
                          </Box>
                          <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1.5, background: '#efebe9', border: '1px solid #d7ccc8' }}>
                            <span style={{ fontSize: '0.68rem', color: '#6d4c41' }}>Round Off </span>
                            <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 600, color: '#6d4c41' }}>
                              {ro > 0 ? '+' : '−'}₹{Math.abs(ro).toFixed(2)}
                            </span>
                          </Box>
                        </>
                      );
                    })()}
                    {!isReadOnly && (
                      <FormControlLabel
                        control={<Checkbox size="small" checked={roundOffEnabled}
                          onChange={(e) => setRoundOffEnabled(e.target.checked)}
                          sx={{ color: TEAL, '&.Mui-checked': { color: TEAL }, p: 0.5 }} />}
                        label={<span style={{ fontSize: '0.68rem', color: '#718096' }}>Round off net payable to nearest ₹</span>}
                        sx={{ m: 0 }}
                      />
                    )}
                    {/* show original if returns exist */}
                    {isReadOnly && originalTotals && originalTotals.net_amount > totals.net_amount && (
                      <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1.5, background: '#fff3e0', border: '1px solid #ffcc80' }}>
                        <span style={{ fontSize: '0.68rem', color: '#e65100' }}>Original </span>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#e65100', textDecoration: 'line-through' }}>₹{Number(originalTotals.net_amount).toFixed(2)}</span>
                        <span style={{ fontSize: '0.65rem', color: '#e65100', marginLeft: 4 }}>→ Returned: ₹{(Number(originalTotals.net_amount) - Number(totals.net_amount)).toFixed(2)}</span>
                      </Box>
                    )}
                  </Box>
                  {/* Net amount + paid */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 2, borderLeft: `3px solid ${TEAL}` }}>
                    {bill?.amount_paid > 0 && (
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: '0.68rem', color: '#2e7d32' }}>Paid</Typography>
                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.85rem', color: '#2e7d32' }}>₹{Number(bill.amount_paid).toFixed(2)}</Typography>
                      </Box>
                    )}
                    {bill?.credit_applied > 0 && (
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: '0.68rem', color: '#7b1fa2' }}>Credit Used</Typography>
                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.85rem', color: '#7b1fa2' }}>₹{Number(bill.credit_applied).toFixed(2)}</Typography>
                      </Box>
                    )}
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: '0.7rem', color: TEAL, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {isReadOnly && originalTotals && originalTotals.net_amount > totals.net_amount ? 'Effective Net' : 'Net Amount'}
                      </Typography>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.2rem', color: TEAL, lineHeight: 1.1 }}>₹{Number(totals.net_amount).toFixed(2)}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
            )}

            {/* Bill History: payments, returns, credit notes — for read-only bills */}
            {isReadOnly && bill && (
              <Card sx={{ p: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>
                <SectionHeader icon={<PaymentIcon sx={{ color: TEAL, fontSize: 18 }} />} title="Bill History" />

                {/* Payments */}
                {(bill.payments || []).length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>Payments Received</Typography>
                    {(bill.payments || []).map((p, i) => (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, px: 1.2, borderRadius: 1.5, background: i % 2 === 0 ? '#f8fafb' : '#fff', border: '1px solid #eef2f7', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={p.payment_mode} size="small"
                            sx={{ fontSize: '0.62rem', fontWeight: 700, height: 18, background: { CASH: '#e8f5e9', UPI: '#e3f2fd', CARD: '#f3e5f5', ADVANCE: '#fff3e0' }[p.payment_mode] || '#f0f0f0', color: { CASH: '#2e7d32', UPI: '#1565c0', CARD: '#6a1b9a', ADVANCE: '#e65100' }[p.payment_mode] || '#555' }} />
                          {p.reference_no && <Typography sx={{ fontSize: '0.7rem', color: '#888', fontFamily: 'monospace' }}>Ref: {p.reference_no}</Typography>}
                          <Typography sx={{ fontSize: '0.7rem', color: '#aaa' }}>{fmtDate(p.payment_date)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem', color: '#2e7d32' }}>₹{Number(p.amount).toFixed(2)}</Typography>
                          <Chip label={p.status} size="small" sx={{ fontSize: '0.6rem', height: 16, background: p.status === 'PAID' ? '#e8f5e9' : '#fdecea', color: p.status === 'PAID' ? '#2e7d32' : '#c62828' }} />
                          <Tooltip title="Print Receipt" arrow>
                            <IconButton size="small" onClick={() => printPaymentReceipt({ ...p, bill }, selectedPharmacy)}
                              sx={{ p: 0.4, color: TEAL, '&:hover': { bgcolor: `${TEAL}14` } }}>
                              <PrintIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Returns */}
                {(bill.returns || []).length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>Returns</Typography>
                    {(bill.returns || []).map((ret, i) => (
                      <Box key={i} sx={{ py: 0.75, px: 1.2, borderRadius: 1.5, background: '#fff3e0', border: '1px solid #ffcc80', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label={ret.refund_method || 'CASH'} size="small" sx={{ fontSize: '0.62rem', fontWeight: 700, height: 18, background: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80' }} />
                            {ret.return_no && <Typography sx={{ fontSize: '0.7rem', color: '#e65100', fontWeight: 700, fontFamily: 'monospace' }}>{ret.return_no}</Typography>}
                            <Typography sx={{ fontSize: '0.7rem', color: '#888' }}>{fmtDate(ret.return_date)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem', color: '#e65100' }}>−₹{Number(ret.return_amount).toFixed(2)}</Typography>
                            <Tooltip title="Print Return Note" arrow>
                              <IconButton size="small" onClick={() => printReturnNote({ ...ret, original_bill: bill }, selectedPharmacy)}
                                sx={{ p: 0.4, color: '#e65100', '&:hover': { bgcolor: 'rgba(230,81,0,0.1)' } }}>
                                <PrintIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        {ret.reason && <Typography sx={{ fontSize: '0.68rem', color: '#888', mt: 0.25 }}>Reason: {ret.reason}</Typography>}
                        {(ret.items || []).length > 0 && (
                          <Box sx={{ mt: 0.5 }}>
                            {ret.items.map((ri, j) => {
                              const billItem = cartItems.find(b => b.id === ri.irm_pharm_bill_items_id);
                              return (
                                <Typography key={j} sx={{ fontSize: '0.67rem', color: '#777', fontFamily: 'monospace' }}>
                                  · {billItem?.item_name || `Item #${ri.irm_pharm_bill_items_id}`}: qty {ri.return_qty} → ₹{Number(ri.refund_amount).toFixed(2)}
                                </Typography>
                              );
                            })}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Credit Notes */}
                {(bill.credit_notes || []).length > 0 && (
                  <Box>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75 }}>Credit Notes</Typography>
                    {(bill.credit_notes || []).map((cn, i) => (
                      <Box key={i} sx={{ py: 0.75, px: 1.2, borderRadius: 1.5, background: '#f3e5f5', border: `1px solid ${cn.status === 'OPEN' ? '#ce93d8' : '#e0e0e0'}`, mb: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#6a1b9a' }}>{cn.credit_note_no}</Typography>
                            <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>{fmtDate(cn.credit_note_date)} · {cn.reason || ''}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Chip label={cn.status} size="small" sx={{ fontSize: '0.6rem', height: 16, mb: 0.3, background: cn.status === 'OPEN' ? '#e8f5e9' : '#ede7f6', color: cn.status === 'OPEN' ? '#2e7d32' : '#6a1b9a' }} />
                            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.82rem', color: '#6a1b9a', display: 'block' }}>₹{Number(cn.balance_amount).toFixed(2)}</Typography>
                            {cn.status !== 'OPEN' && <Typography sx={{ fontSize: '0.65rem', color: '#aaa' }}>of ₹{Number(cn.amount).toFixed(2)}</Typography>}
                          </Box>
                        </Box>
                        <Box sx={{ mt: 0.75, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.75 }}>
                          {cn.status === 'OPEN' && (
                            <Button size="small" variant="outlined"
                              onClick={() => setDebitNoteCn(cn)}
                              sx={{
                                fontSize: '0.68rem', fontWeight: 700, textTransform: 'none',
                                borderColor: '#7b1fa2', color: '#7b1fa2', borderRadius: 2,
                                py: 0.3, px: 1.2,
                                '&:hover': { background: '#f3e5f5', borderColor: '#4a148c', color: '#4a148c' },
                              }}>
                              Issue Debit Note
                            </Button>
                          )}
                          <Tooltip title="Print Credit Note" arrow>
                            <IconButton size="small" onClick={() => printCreditNote({ ...cn, original_bill: bill }, selectedPharmacy)}
                              sx={{ p: 0.4, color: '#6a1b9a', '&:hover': { bgcolor: 'rgba(106,27,154,0.1)' } }}>
                              <PrintIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Cancel info */}
                {bill.status === 'CANCELLED' && bill.cancel_reason && (
                  <Box sx={{ mt: 1, p: 1, borderRadius: 1.5, background: '#fdecea', border: '1px solid #ffcdd2' }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#c62828', fontWeight: 600 }}>Cancellation reason: {bill.cancel_reason}</Typography>
                    {bill.refund_method && <Typography sx={{ fontSize: '0.68rem', color: '#c62828', mt: 0.25 }}>Refunded via: {bill.refund_method.replace('_', ' ')}</Typography>}
                    {bill.cancelled_at && <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>{fmtDate(bill.cancelled_at)}</Typography>}
                  </Box>
                )}

                {(bill.payments || []).length === 0 && (bill.returns || []).length === 0 && (bill.credit_notes || []).length === 0 && !bill.cancel_reason && (
                  <Typography sx={{ fontSize: '0.75rem', color: '#aaa', textAlign: 'center', py: 1 }}>No payment or return history</Typography>
                )}
              </Card>
            )}
          </Box>
        </Box>
      </MDBox>

      <PaymentDialog open={payDialog} netAmount={totals.net_amount} creditNotes={creditNotes} onClose={() => setPayDialog(false)} onConfirm={handleFinalize} />
      <CancelDialog open={cancelDialog} bill={bill} onClose={() => setCancelDialog(false)} onConfirm={handleCancel} />
      <ReturnDialog open={returnDialog} bill={bill} onClose={() => setReturnDialog(false)} onConfirm={handleReturn} />
      <DebitNoteDialog
        open={Boolean(debitNoteCn)}
        creditNote={debitNoteCn}
        contactId={bill?.irm_contacts_id || patient?.irm_contacts_id}
        onClose={() => setDebitNoteCn(null)}
        onSuccess={() => {
          setDebitNoteCn(null);
          // Reload bill to refresh credit note statuses
          if (!isNew) {
            api.pharmacy.getBill(id).then(b => setBill(b)).catch(() => {});
          }
        }}
      />

      <TimelineDialog
        open={showTimeline}
        onClose={() => setShowTimeline(false)}
        title="Bill Journey"
        subtitle={bill ? `${bill.bill_no} · ${bill.patient_name || ''}` : ''}
        events={timelineEvents}
        loading={timelineLoading}
      />

      <Footer />
    </DashboardLayout>
  );
}
